// src/components/Results.js

import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Button,
  Divider,
  Select,
  MenuItem,
  FormControl,
  Slider,
  Popover,
  TextField,
  IconButton,
} from "@mui/material";
import { Carousel } from "react-responsive-carousel";
import { Chip } from "@mui/material";
import "react-responsive-carousel/lib/styles/carousel.min.css"; // Import carousel styles
import { Skeleton } from "@mui/material";
import { CircularProgress } from "@mui/material"; // Add this import if not present
import { useMediaQuery, useTheme } from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import CommentIcon from "@mui/icons-material/Comment";
import HomeIcon from "@mui/icons-material/Home";
import { darken } from "@mui/system";
import AnalyzeAirbnbModalDashboard from "./AnalyzeAirbnbModal";
import { AMENITIES_MAP } from "../utils/amenitiesMap";
import MapView from "./MapView";
import iataLookup from "../iata_lookup.json";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { formatPrice } from "../utils/formatPrice";
import axios from "axios";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import IosShareIcon from "@mui/icons-material/IosShare";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import BookmarkRoundedIcon from "@mui/icons-material/BookmarkRounded";
import Tooltip from "@mui/material/Tooltip";
import { Paper } from "@mui/material";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import { fetchCityImage } from "../utils/fetchCityImage";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { format } from "date-fns";
import { currencyOptions } from "../utils/formatPrice";
import TripPlannerModal from "./TripPlannerModal"; // Adjust path as needed
import MapIcon from "@mui/icons-material/Map";
import ViewListIcon from "@mui/icons-material/ViewList";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";

const ImageCarouselWithDetails = ({
  listings = [], // Default to empty array if undefined
  departureDate,
  returnDate,
  totalStayPrice,
  currency,
  isPro,
}) => {
  const [currentListing, setCurrentListing] = useState(
    listings.length > 0 ? listings[0] : null
  );

  const [analyzeOpen, setAnalyzeOpen] = useState(false);

  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // Detect mobile view

  const handleCarouselChange = (index) => {
    setCurrentListing(listings[index]);
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
    }).format(date);
  };
  // (Inside ImageCarouselWithDetails, before the return)
  const handleNavigateAnalyze = (direction) => {
    if (!currentListing || !listings) return;

    // Find index of current listing in the listings array
    const currentIndex = listings.findIndex((s) => s.id === currentListing.id);

    if (currentIndex === -1) return;

    let newIndex = direction === "next" ? currentIndex + 1 : currentIndex - 1;

    // Safety check
    if (newIndex >= 0 && newIndex < listings.length) {
      setCurrentListing(listings[newIndex]);
    }
  };
  // -----------------------------------------------------

  if (!listings || listings.length === 0) {
    return (
      <Box sx={{ textAlign: "center", mt: 3 }}>
        <Typography variant="h5" sx={{ color: "text.secondary", mb: 2 }}>
          No listings available for the selected stay type.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "500px",
        mx: "auto",
        textAlign: "center",
      }}
    >
      {/* Display Stay Dates */}
      <Typography variant="body1" sx={{ mb: 1, color: "text.secondary" }}>
        {departureDate && returnDate
          ? `${formatDate(departureDate)} - ${formatDate(returnDate)}`
          : "Dates not set"}
      </Typography>

      {/* Listing Name + Price + Rating — single clickable group */}
      <Box
        component="a"
        href={currentListing.deeplink}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          display: "block",
          textDecoration: "none",
          color: "inherit",
          cursor: "pointer",
          "&:hover .listing-title": { textDecoration: "underline" },
          "&:hover .listing-price": { textDecoration: "underline" },
          "&:hover .listing-rating": { textDecoration: "underline", color: "primary.main" },
        }}
      >
        <Typography
          className="listing-title"
          variant="h3"
          gutterBottom
          sx={{
            fontWeight: "bold",
            fontSize: { xs: "1.2rem", sm: "1.2rem" },
            mb: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            width: "100%",
            maxWidth: { xs: "300px", sm: "350px" },
            textAlign: "center",
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          {currentListing.name}
        </Typography>

        {/* Pricing Details */}
        <Typography className="listing-price" variant="body1" sx={{ mb: 0 }}>
        {(() => {
          // Check if 'total' is null and only 'rate' is provided (likely a monthly rate)
          if (
            currentListing?.price?.total === null &&
            currentListing?.price?.rate
          ) {
            const estimatedNightlyRate = Math.round(
              currentListing.price.rate / 30
            );
            const nights = Math.ceil(
              (new Date(returnDate) - new Date(departureDate)) /
                (1000 * 60 * 60 * 24)
            );
            const estimatedTotal = estimatedNightlyRate * nights;

            return `$${estimatedNightlyRate}/night x ${nights} nights = $${estimatedTotal}`;
          }

          // Regular calculation for listings with a valid 'total'
          const nightsInfo = currentListing.price.priceItems?.find((item) =>
            item.title.includes("nights")
          );
          const numberOfNights = nightsInfo
            ? parseInt(nightsInfo.title.match(/\d+ (?=nights)/)?.[0], 10)
            : Math.ceil(
                (new Date(returnDate) - new Date(departureDate)) /
                  (1000 * 60 * 60 * 24)
              );

          const ratePerNight = numberOfNights
            ? Math.round(currentListing.price.total / numberOfNights)
            : currentListing.price.rate;

          const formattedRatePerNight = new Intl.NumberFormat("en-US").format(
            ratePerNight
          );
          const formattedTotalPrice = new Intl.NumberFormat("en-US").format(
            Math.round(currentListing.price.total)
          );

          const symbolOnly = formatPrice(0, currency, false)
            .replace(/\d/g, "")
            .trim();
          return `${symbolOnly}${formattedRatePerNight}/night x ${numberOfNights} nights = ${symbolOnly}${formattedTotalPrice}`;
        })()}
        </Typography>
        {/* Rating and Reviews */}
        <Typography
          className="listing-rating"
          variant="body2"
          sx={{
            color: "text.secondary",
            fontSize: "1rem",
            fontWeight: "light",
            mt: 0,
            mb: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "2px",
          }}
        >
          {currentListing.rating && currentListing.reviewsCount ? (
            <>
              <StarIcon sx={{ fontSize: "1rem" }} />
              {`${currentListing.rating} (${currentListing.reviewsCount} reviews)`}
            </>
          ) : (
            "No reviews available"
          )}
        </Typography>
      </Box>

      {/* Carousel of Stay Listings */}
      <Carousel
        showThumbs={false}
        infiniteLoop
        autoPlay={!analyzeOpen}
        interval={2000}
        transitionTime={600}
        showStatus={false}
        onChange={handleCarouselChange}
        centerMode={true}
        centerSlidePercentage={100}
        renderArrowPrev={(onClickHandler, hasPrev, label) =>
          hasPrev && (
            <button
              type="button"
              onClick={onClickHandler}
              style={{
                position: "absolute",
                left: isMobile ? 5 : 15, // Adjust for mobile
                top: "50%",
                transform: "translateY(-50%)",
                background: "rgba(0, 0, 0, 0.5)", // Background color
                color: "white", // Arrow color
                border: "none",
                borderRadius: "50%",
                width: "44px",
                height: "44px",
                fontSize: "20px", // Arrow icon size
                cursor: "pointer",
                zIndex: 2,
              }}
            >
              ‹
            </button>
          )
        }
        renderArrowNext={(onClickHandler, hasNext, label) =>
          hasNext && (
            <button
              type="button"
              onClick={onClickHandler}
              style={{
                position: "absolute",
                right: isMobile ? 5 : 15, // Adjust for mobile
                top: "50%",
                transform: "translateY(-50%)",
                background: "rgba(0, 0, 0, 0.5)", // Background color
                color: "white", // Arrow color
                border: "none",
                borderRadius: "50%",
                width: "44px",
                height: "44px",
                fontSize: "20px", // Arrow icon size
                cursor: "pointer",
                zIndex: 2,
              }}
            >
              ›
            </button>
          )
        }
      >
        {listings.map((listing, index) => (
          <div
            key={index}
            style={{
              width: "100%",
              height: "300px",
              overflow: "hidden",
              position: "relative",
            }}
          >
            <a href={listing.deeplink} target="_blank" rel="noopener noreferrer" style={{ display: "block", height: "100%" }}>
              <img
                src={listing.images[0].replace(/^"|"$/g, "")}
                alt={`Listing ${index + 1}`}
                style={{
                  width: "100%",
                  height: "300px",
                  objectFit: "cover",
                  borderRadius: "8px",
                  cursor: "pointer",
                }}
              />
            </a>
          </div>
        ))}
      </Carousel>

      {/* Analyze Airbnb Button */}
      {currentListing && (
        <>
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <Button
              variant="contained"
              size="medium"
              color="primary"
              startIcon={<AutoAwesomeIcon />}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 500,
                fontSize: "1rem",
                px: 2,
                py: 0.8,
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                },
              }}
              onClick={() => {
                // 1. Fire Google Analytics Event
                if (window.gtag) {
                  window.gtag("event", "click_run_analyzer", {
                    listing_name: currentListing?.name || "Unknown Listing",
                    city: currentListing?.city || "Unknown City",
                  });
                }
                // 2. Perform original action
                setAnalyzeOpen(true);
              }}
            >
              Analyze Listing with AI
            </Button>
          </Box>

          <AnalyzeAirbnbModalDashboard
            open={analyzeOpen}
            onClose={() => setAnalyzeOpen(false)}
            listingId={currentListing.id}
            price={currentListing.price}
            departureDate={departureDate}
            returnDate={returnDate}
            isPro={isPro}
            amenityIds={currentListing.amenityIds}
            currency={currency}
            // ✅ NEW PROPS: Pass navigation logic
            onNext={() => handleNavigateAnalyze("next")}
            onPrev={() => handleNavigateAnalyze("prev")}
            hasNext={
              listings.findIndex((s) => s.id === currentListing.id) <
              listings.length - 1
            }
            hasPrev={listings.findIndex((s) => s.id === currentListing.id) > 0}
          />
        </>
      )}
      {/* Total Stay Price Typography */}
      <Typography
        variant="h6"
        sx={{
          fontWeight: "bold",
          fontSize: "3rem",
          mr: 0,
          mt: 2,
          mb: 0,
        }}
      >
        ~{formatPrice(totalStayPrice, currency)}
      </Typography>

      {/* View on AirBnB Button */}
      {currentListing && (
        <Box sx={{ mt: 1, textAlign: "center" }}>
          <Button
            href={currentListing.deeplink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              if (window.gtag) {
                window.gtag("event", "click_view_airbnb", {
                  listing_name: currentListing.name || "Unknown Listing",
                  city: currentListing.city || "Unknown City",
                  price: currentListing.price?.total || currentListing.price,
                });
              }
            }}
            // endIcon={<HotelIcon />} // Add the icon here
            sx={{
              mt: 0,
              mb: 2,
              backgroundColor: "#FF5A5F", // Airbnb's signature color
              color: "white",
              fontWeight: "bold",
              borderRadius: "8px",
              gap: "6px",
              padding: "12px 24px", // Increase padding for better look
              fontSize: "1.2rem",
              transition: "all 0.3s ease-in-out",
              textTransform: "none", // Keep the button text as-is
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)", // Add subtle shadow for depth
              "&:hover": {
                backgroundColor: "#E14850", // Darker shade on hover
                boxShadow: "0 6px 16px rgba(0, 0, 0, 0.3)", // Stronger shadow on hover
                transform: "scale(1.05)", // Slight zoom effect on hover
              },
            }}
          >
            View on AirBnB
            <img
              src="/logo-airbnb.svg" // Reference from the public folder
              alt="Airbnb Logo"
              style={{ width: "24px", height: "24px" }}
            />
          </Button>
        </Box>
      )}
    </Box>
  );
};

/**
 * Results Component
 * Displays flight and stay results.
 */
const Results = ({
  bestItinerary,
  cheapestItinerary,
  fastestItinerary,
  flightResults,
  flightLoading,
  stayLoading,
  sortBy,
  handleSortChange,
  quartiles,
  selectedStayCategory,
  handleStayCategoryChange,
  economyListings,
  standardListings,
  premiumListings,
  handleBackToSearch,
  departureDate,
  submittedDepartureDate,
  submittedReturnDate,
  returnDate,
  handleClearStayFilter,
  loading,
  airbnbType,
  noAirbnbFound,
  noFlightsFound,
  submittedOrigin,
  origin,
  submittedDestination,
  submittedDestination: destination,
  originIATA,
  destIATA,
  flightType,
  searchType,
  minReviewCount,
  minReviewScore,
  tofly,
  setAirbnbType,
  setMinReviewCount,
  setMinReviewScore,
  advancedFilters,
  setAdvancedFilters,
  currency,
  setFiltersOpen,
  isPro,
  flightPriceHistory,
  enrichReturn,
  onShareSearch,
  onSaveSearch,
  isSearchSaved,
  ...props
}) => {
  const [isLoadingDirectBooking, setIsLoadingDirectBooking] = useState(false);
  const theme = useTheme(); // ✅ Get theme
  const [showSkeleton, setShowSkeleton] = useState(false);
  const [showMapView, setShowMapView] = useState(false);
  // ✅ NEW: State to track which listing was clicked on the map
  const [mapListing, setMapListing] = useState(null);
  const [mapAnalyzeOpen, setMapAnalyzeOpen] = useState(false);
  // 👇 add this INSIDE the Results component, after useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [plannerOpen, setPlannerOpen] = useState(false);
  
  const [isCustomizeExpanded, setIsCustomizeExpanded] = useState(false); // false | "flights" | "stays"
  const [customItinerary, setCustomItinerary] = useState(null);
  const [staysSort, setStaysSort] = useState("rating"); // "rating" | "reviews" | "price_low" | "price_high"
  const [staysTier, setStaysTier] = useState("all"); // "all" | "economy" | "standard" | "premium"
  const [staysPriceRange, setStaysPriceRange] = useState(null); // null = no filter, else [min, max]
  const [priceFilterAnchor, setPriceFilterAnchor] = useState(null);

  // Apply flight stops filter client-side
  const filteredFlightResults = useMemo(() => {
    const stops = advancedFilters?.flightStops;
    if (!stops || stops.length === 0) return flightResults;
    return flightResults.filter((f) => {
      const layoverCount = f.layovers?.length || 0;
      return stops.some((s) =>
        s === "nonstop" ? layoverCount === 0 :
        s === "1stop"   ? layoverCount === 1 :
        layoverCount >= 2
      );
    });
  }, [flightResults, advancedFilters?.flightStops]);

  // Expanded flights column — inline-expandable itinerary list with stops/duration filters
  const [expandedFlightId, setExpandedFlightId] = useState(null);
  const [expStopsFilter, setExpStopsFilter] = useState("any"); // "any" | "nonstop" | "1" | "2plus"
  const [expDurationMax, setExpDurationMax] = useState(null);  // max outbound minutes, null = no filter
  const [durationFilterAnchor, setDurationFilterAnchor] = useState(null);
  const [enrichingFlightId, setEnrichingFlightId] = useState(null); // itinerary currently lazy-loading its return

  // Outbound duration bounds across the available itineraries — for the slider
  const durationBounds = useMemo(() => {
    const mins = filteredFlightResults
      .map((f) => f.durationRaw)
      .filter((m) => Number.isFinite(m) && m > 0);
    return mins.length
      ? { min: Math.floor(Math.min(...mins)), max: Math.ceil(Math.max(...mins)) }
      : { min: 0, max: 0 };
  }, [filteredFlightResults]);

  // filteredFlightResults + the expanded-column stops/duration filters, sorted by price
  const expandedFlights = useMemo(() => {
    return [...filteredFlightResults]
      .filter((f) => {
        const stops = Math.max(0, (f?.legs?.length || 1) - 1);
        if (expStopsFilter === "nonstop" && stops !== 0) return false;
        if (expStopsFilter === "1" && stops !== 1) return false;
        if (expStopsFilter === "2plus" && stops < 2) return false;
        if (expDurationMax != null && Number.isFinite(f.durationRaw) && f.durationRaw > expDurationMax) return false;
        return true;
      })
      .sort((a, b) => (a.price || 0) - (b.price || 0));
  }, [filteredFlightResults, expStopsFilter, expDurationMax]);

  useEffect(() => {
    setCustomItinerary(null);
  }, [sortBy]);

  const hasFlightsSection =
    searchType !== "staysOnly" && !(searchType === "explore" && !tofly);

  const hasStaysSection = true;

  const showMobileToggle = isMobile && hasFlightsSection && hasStaysSection;

  // summaries for sticky header
  const currencySymbol = formatPrice(0, currency, false).replace(
    /\d|[.,\s]/g,
    ""
  );

  const cheapestFlight =
    Array.isArray(flightResults) && flightResults.length
      ? Math.min(
          ...flightResults.map((f) => f.price ?? Number.POSITIVE_INFINITY)
        )
      : null;

  const staysCount =
    (economyListings?.length ?? 0) +
    (standardListings?.length ?? 0) +
    (premiumListings?.length ?? 0);

  const medianNightly = (() => {
    const candidates = [quartiles?.Q2, quartiles?.Q1, quartiles?.Q3].filter(
      (v) => Number.isFinite(v)
    );
    return candidates.length ? Math.round(candidates[0]) : null;
  })();

  const [mobilePane, setMobilePane] = useState("stays");

  useEffect(() => {
    if (!isMobile) return;
    // Always default to stays when entering mobile mode, regardless of flight availability
    setMobilePane("stays");
  }, [isMobile, hasFlightsSection]);

  // Collapse any expanded row when leaving the flights view
  useEffect(() => {
    if (isCustomizeExpanded !== "flights") {
      setExpandedFlightId(null);
    }
  }, [isCustomizeExpanded]);



  const flightStopsFiltered = (advancedFilters?.flightStops?.length || 0) > 0 && filteredFlightResults.length === 0 && flightResults.length > 0;

  const selectedItinerary = useMemo(() => {
    if (customItinerary) return customItinerary;
    if (sortBy === "cheapest")
      return filteredFlightResults.find((f) => f.isCheapest) ?? filteredFlightResults[0];
    if (sortBy === "fastest")
      return filteredFlightResults.find((f) => f.isFastest) ?? filteredFlightResults[0];
    return filteredFlightResults.find((f) => f.isBest) ?? filteredFlightResults[0];
  }, [sortBy, filteredFlightResults, customItinerary]);

  const getCityByIATA = (iataCode) => {
    return iataLookup[iataCode]?.city || null;
  };

  const formatDurationMins = (m) => {
    if (!Number.isFinite(m)) return "";
    const h = Math.floor(m / 60);
    const mm = Math.round(m % 60);
    return h > 0 ? `${h}h ${mm}m` : `${mm}m`;
  };

  const isDirect =
    filteredFlightResults.length > 0 && selectedItinerary?.legs?.length <= 1;

  const isReturnDirect =
    filteredFlightResults.length > 0 && selectedItinerary?.returnLegs?.length <= 1;

  const formatTime = (dateString) => {
    if (!dateString || typeof dateString !== "string") {
      console.error("formatTime received invalid input:", dateString);
      return "Invalid Time";
    }

    let date = new Date(dateString); // Try direct parsing (handles "YYYY-M-D HH:MM")

    if (isNaN(date.getTime())) {
      // Fallback for "DD-MM-YYYY HH:MM AM/PM" format
      // Ensure splitting is safe
      const parts = dateString.split(" ");
      if (parts.length < 2) {
        // Needs at least date and time part, AM/PM might make it 3
        console.error(
          "formatTime: Invalid format for DD-MM-YYYY HH:MM AM/PM fallback:",
          dateString
        );
        return "Invalid Date";
      }

      const datePart = parts[0];
      const timePartAPI =
        parts.length === 3 ? `${parts[1]} ${parts[2]}` : parts[1]; // Handles "HH:MM AM/PM" or "HH:MM"

      const datePartSegments = datePart.split("-");
      if (datePartSegments.length !== 3) {
        console.error(
          "formatTime: Invalid date part segments for DD-MM-YYYY fallback:",
          datePart
        );
        return "Invalid Date";
      }
      const [day, month, year] = datePartSegments;

      const twentyFourHourTime = convertTo24Hr(timePartAPI);
      const isoString = `${year}-${month}-${day}T${twentyFourHourTime}`;
      date = new Date(isoString);
    }

    if (isNaN(date.getTime())) {
      console.error(
        "formatTime: Still an invalid date after all attempts for:",
        dateString
      );
      return "Invalid Date";
    }

    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  // In Results.js

  const isNextDay = (start, end) => {
    if (
      !start ||
      !end ||
      typeof start !== "string" ||
      typeof end !== "string"
    ) {
      return false;
    }

    const toDateRobust = (ds) => {
      let dateObj = new Date(ds); // Try direct parsing

      if (isNaN(dateObj.getTime())) {
        // Fallback for "DD-MM-YYYY HH:MM AM/PM"
        const parts = ds.split(" ");
        if (parts.length < 2) {
          return new Date(null); // Invalid date
        }
        const datePart = parts[0];
        const timePartAPI =
          parts.length === 3 ? `${parts[1]} ${parts[2]}` : parts[1];

        const datePartSegments = datePart.split("-");
        if (datePartSegments.length !== 3) {
          return new Date(null); // Invalid date
        }
        const [day, month, year] = datePartSegments;

        const twentyFourHourTime = convertTo24Hr(timePartAPI);
        const isoString = `${year}-${month}-${day}T${twentyFourHourTime}`;
        dateObj = new Date(isoString);
      }
      return dateObj;
    };

    const d1 = toDateRobust(start);
    const d2 = toDateRobust(end);

    if (isNaN(d1.getTime()) || isNaN(d2.getTime())) {
      console.error(
        "isNextDay: Invalid date objects from toDateRobust. Start:",
        start,
        "End:",
        end
      );
      return false;
    }

    // Check if d2 is on a later day than d1
    const d1Year = d1.getFullYear();
    const d1Month = d1.getMonth();
    const d1Day = d1.getDate();

    const d2Year = d2.getFullYear();
    const d2Month = d2.getMonth();
    const d2Day = d2.getDate();

    return (
      d2Year > d1Year ||
      (d2Year === d1Year && d2Month > d1Month) ||
      (d2Year === d1Year && d2Month === d1Month && d2Day > d1Day)
    );
  };

  // In Results.js

  const buildKayakURL = (
    origin,
    destination,
    departureDate,
    returnDate = null
  ) => {
    const formatDate = (date) => {
      const d = new Date(date);
      const year = String(d.getFullYear()).slice(2);
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}${month}${day}`;
    };

    const depDate = formatDate(departureDate);

    if (returnDate) {
      const retDate = formatDate(returnDate);
      return `https://www.kayak.com/flights/${origin}-${destination}/${depDate}/${retDate}`;
    }

    return `https://www.kayak.com/flights/${origin}-${destination}/${depDate}`;
  };

  const convertTo24Hr = (timeStr) => {
    // Check if timeStr is already in a 24-hour like format or doesn't have AM/PM
    if (!timeStr.includes(" AM") && !timeStr.includes(" PM")) {
      const [hoursStr, minutesStr] = timeStr.split(":");
      const hours = parseInt(hoursStr, 10);
      const minutes = parseInt(minutesStr, 10);
      if (!isNaN(hours) && !isNaN(minutes)) {
        return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:00`;
      }
      // If it's not parsable as simple HH:MM, return a default or handle error
      console.error(
        "convertTo24Hr received unexpected format without AM/PM:",
        timeStr
      );
      return "00:00:00"; // Fallback for unexpected formats without AM/PM
    }

    const [time, modifier] = timeStr.split(" ");
    let [hours, minutes] = time.split(":").map(Number);

    if (isNaN(hours) || isNaN(minutes)) {
      console.error("convertTo24Hr: Invalid time parts", timeStr);
      return "00:00:00"; // Fallback for invalid time parts
    }

    if (modifier.toUpperCase() === "PM" && hours < 12) hours += 12;
    if (modifier.toUpperCase() === "AM" && hours === 12) hours = 0; // Midnight case

    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:00`;
  };

  useEffect(() => {
    if (selectedStayCategory === "economy" && economyListings.length === 0) {
      if (standardListings.length > 0) {
        handleStayCategoryChange(null, "standard");
      } else if (premiumListings.length > 0) {
        handleStayCategoryChange(null, "premium");
      }
    } else if (
      selectedStayCategory === "standard" &&
      standardListings.length === 0 &&
      premiumListings.length > 0
    ) {
      handleStayCategoryChange(null, "premium");
    }
  }, [
    economyListings.length,
    standardListings.length,
    premiumListings.length,
    selectedStayCategory,
    handleStayCategoryChange,
  ]);

  // Helper function to get the filter label
  const getFilterLabel = (type, minReviewCount, minReviewScore) => {
    const filters = [];

    const parsedReviewScore = parseFloat(minReviewScore);

    // Include minimum review count
    if (minReviewCount !== 5) {
      filters.push(
        <Box
          key="reviews"
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
        >
          > {minReviewCount} <CommentIcon sx={{ fontSize: "1rem" }} />
        </Box>
      );
    }

    // Include minimum review score
    if (!isNaN(parsedReviewScore) && parsedReviewScore !== 4.0) {
      filters.push(
        <Box
          key="rating"
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
        >
          {parsedReviewScore.toFixed(2)} <StarIcon sx={{ fontSize: "1rem" }} />
        </Box>
      );
    }

    // Include Airbnb type filter
    if (type === "entire_home") {
      filters.push(
        <Box
          key="entire_home"
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
        >
          Entire Homes <HomeIcon sx={{ fontSize: "1rem" }} />
        </Box>
      );
    } else if (type === "private_room") {
      filters.push(
        <Box
          key="private_room"
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
        >
          Private Rooms <HomeIcon sx={{ fontSize: "1rem" }} />
        </Box>
      );
    }

    // Return the filters with separators if there are active filters
    return filters.length > 0 ? (
      <>
        {filters.map((filter, idx) => (
          <React.Fragment key={idx}>
            {filter}
            {idx < filters.length - 1 && (
              <Typography
                component="span"
                sx={{
                  margin: "0 8px",
                  color: "white",
                  fontSize: "1rem",
                  fontWeight: 300,
                }}
              >
                |
              </Typography>
            )}
          </React.Fragment>
        ))}
      </>
    ) : (
      ""
    );
  };

  useEffect(() => {
    if (loading) {
      const timer = setTimeout(() => {
        setShowSkeleton(true);
      }, 2000); // Show skeleton after 2 seconds

      return () => clearTimeout(timer); // Cleanup on unmount
    } else {
      setShowSkeleton(false);
    }
  }, [loading]);

  const loadingPhrases = [
    `Finding flights to ${submittedDestination?.city || "your destination"}...`,
    "Checking stay prices...",
    "Crunching the numbers...",
    "Finding the best deals...",
    "Almost ready...",
  ];
  const [loadingPhraseIdx, setLoadingPhraseIdx] = useState(0);
  const bothLoaded = !flightLoading && !stayLoading;
  useEffect(() => {
    if (bothLoaded) { setLoadingPhraseIdx(0); return; }
    const interval = setInterval(() => setLoadingPhraseIdx((i) => (i + 1) % loadingPhrases.length), 2500);
    return () => clearInterval(interval);
  }, [bothLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const [destinationImage, setDestinationImage] = useState(null);
  useEffect(() => {
    if (!submittedDestination?.city) return;
    setDestinationImage(null);
    fetchCityImage(submittedDestination.city, submittedDestination.country_name || "").then(setDestinationImage);
  }, [submittedDestination?.city, submittedDestination?.country_name]);

  if (searchType !== "explore" && loading && !showSkeleton) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100%", // Fill the parent Card's height
        }}
      >
        <CircularProgress size={100} thickness={5} color="white" />
        <Typography
          variant="h6"
          sx={{
            mt: 2,
            color: "white", // Make the text white
            textAlign: "center", // Center align the text
          }}
        >
          Searching...
        </Typography>
      </Box>
    );
  }

  const showSummary =
    searchType !== "staysOnly" && !(searchType === "explore" && !tofly);

  const selectedFlightPrice = selectedItinerary?.price || bestItinerary?.price || cheapestItinerary?.price || 0;
  const selectedStayPrice =
    selectedStayCategory === "economy"
      ? quartiles?.Q1 || 0
      : selectedStayCategory === "premium"
      ? quartiles?.Q3 || 0
      : quartiles?.Q2 || 0;
  const totalTripCost = Math.round(selectedFlightPrice + selectedStayPrice);
  const isCostReady =
    !flightLoading &&
    !stayLoading &&
    selectedFlightPrice > 0 &&
    selectedStayPrice > 0;

  const effectiveDep = submittedDepartureDate || departureDate;
  const effectiveRet = submittedReturnDate || returnDate;

  // 1. Create a master list with "Tier" tags for coloring
  const allMapListings = [
    ...economyListings.map((l) => ({ ...l, tier: "economy" })),
    ...standardListings.map((l) => ({ ...l, tier: "standard" })),
    ...premiumListings.map((l) => ({ ...l, tier: "premium" })),
  ];

  // 2. Add this navigation handler
  const handleMapNavigate = (direction) => {
    if (!mapListing || allMapListings.length === 0) return;

    // ... (rest of logic remains the same)
    const currentIndex = allMapListings.findIndex(
      (l) => l.id === mapListing.id
    );
    if (currentIndex === -1) return;

    const newIndex = direction === "next" ? currentIndex + 1 : currentIndex - 1;

    if (newIndex >= 0 && newIndex < allMapListings.length) {
      setMapListing(allMapListings[newIndex]);
    }
  };

  const tripDays =
    effectiveDep && effectiveRet
      ? Math.ceil(
          (new Date(effectiveRet) - new Date(effectiveDep)) /
            (1000 * 60 * 60 * 24)
        )
      : 0;

  if (loading && showSkeleton) {
    return (
      <Box sx={{ mt: 4, px: 1 }}>
        <Skeleton variant="text" width="55%" height={36} sx={{ mx: "auto", mb: 4 }} animation="wave" />
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2 }}>
          <Box sx={{ flex: 1, p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Skeleton variant="circular" width={40} height={40} />
              <Skeleton variant="text" width={130} height={28} />
            </Box>
            <Skeleton variant="rectangular" height={56} sx={{ mb: 2, borderRadius: 1 }} animation="wave" />
            <Skeleton variant="text" width="35%" height={40} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={48} sx={{ mb: 1, borderRadius: 1 }} animation="wave" />
            <Skeleton variant="rectangular" height={48} sx={{ mb: 1, borderRadius: 1 }} animation="wave" />
            <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 1 }} animation="wave" />
          </Box>
          <Box sx={{ flex: 1, p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
            <Skeleton variant="rectangular" height={300} sx={{ mb: 2, borderRadius: 1 }} animation="wave" />
            <Skeleton variant="text" width="80%" height={28} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="60%" height={22} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="40%" height={20} />
          </Box>
        </Box>
      </Box>
    );
  }
  return (
    <>
      {showMobileToggle && (
        <Paper
          elevation={3}
          sx={{
            position: "sticky",
            top: 30,
            zIndex: 9,
            px: 1.5,
            py: 1.1,
            borderRadius: 2,
            mb: 0,
            backdropFilter: "saturate(180%) blur(10px)",
            backgroundColor:
              theme.palette.mode === "light"
                ? "rgba(255,255,255,0.9)"
                : "rgba(18,18,18,0.9)",
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <ToggleButtonGroup
            size="small"
            exclusive
            value={mobilePane}
            onChange={(_, val) => val && setMobilePane(val)}
            sx={{
              width: "100%",
              "& .MuiToggleButton-root": {
                flex: 1,
                textTransform: "none",
                px: 1,
                py: 0.5,
                minHeight: 110,
                height: 110,
                borderRadius: 2,
                justifyContent: "center",
                overflow: "hidden",
              },
              "& .Mui-selected": {
                fontWeight: 700,
                boxShadow:
                  theme.palette.mode === "light"
                    ? "0 6px 16px rgba(0,0,0,0.14)"
                    : "0 10px 20px rgba(0,0,0,0.45)",
                background:
                  theme.palette.mode === "light"
                    ? "linear-gradient(180deg, rgba(255,255,255,.95), rgba(242,242,242,.95))"
                    : "linear-gradient(180deg, rgba(42,42,46,.95), rgba(28,28,32,.95))",
              },
            }}
          >
            {/* Flights tab */}
            <ToggleButton value="flights">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Box
                  component="img"
                  src="flight-icon.svg"
                  alt="Flights"
                  sx={{ width: isMobile ? 28 : 34, height: isMobile ? 20 : 24, objectFit: "contain" }}
                />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    lineHeight: 1.15,
                  }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>
                    Flights
                  </Typography>
                  <Typography
                    sx={{ opacity: 0.9, mt: 0.25, fontSize: ".95rem" }}
                  >
                    {(() => {
                      const best =
                        bestItinerary?.price ??
                        flightResults?.find((f) => f.isBest)?.price ??
                        cheapestItinerary?.price ??
                        null;
                      return best != null
                        ? formatPrice(Math.round(best), currency, false)
                        : "—";
                    })()}
                  </Typography>
                  {mobilePane !== "flights" && (
                    <Typography
                      sx={{ opacity: 0.6, mt: 0.15, fontSize: ".85rem" }}
                    >
                      Tap to view
                    </Typography>
                  )}
                </Box>
              </Box>
            </ToggleButton>

            {/* Stays tab */}
            <ToggleButton value="stays">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Box
                  component="img"
                  src="stay-icon.svg"
                  alt="Stays"
                  sx={{ width: isMobile ? 28 : 34, height: isMobile ? 20 : 24, objectFit: "contain" }}
                />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    lineHeight: 1.15,
                  }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>
                    Airbnbs
                  </Typography>
                  <Typography
                    sx={{ opacity: 0.9, mt: 0.25, fontSize: ".95rem" }}
                  >
                    {(() => {
                      const nightly = Number.isFinite(quartiles?.Q2)
                        ? Math.round(quartiles.Q2)
                        : null;
                      const nights = (() => {
                        const d1 = new Date(departureDate);
                        const d2 = new Date(returnDate);
                        const ms = d2 - d1;
                        const n = Math.ceil(ms / (1000 * 60 * 60 * 24));
                        return Number.isFinite(n) && n > 0 ? n : 1;
                      })();
                      const total = nightly != null ? nightly : null;
                      return total != null
                        ? `${formatPrice(total, currency, false)} total`
                        : "—";
                    })()}
                  </Typography>
                  {mobilePane !== "stays" && (
                    <Typography
                      sx={{ opacity: 0.6, mt: 0.15, fontSize: ".85rem" }}
                    >
                      Tap to view
                    </Typography>
                  )}
                </Box>
              </Box>
            </ToggleButton>
          </ToggleButtonGroup>
        </Paper>
      )}
      {/* ✅ 4. PASTE THIS UI BLOCK HERE (Before the Cards Box): */}
      {/* ✅ UNIFIED ACTION BLOCK: Cost Summary + AI Trip Planner */}
      <Box sx={{ textAlign: "center", mt: { xs: 4, md: 2 }, mb: 2, maxWidth: "1124px", mx: "auto", width: "100%" }}>
        {/* 1. Cost Summary (Only shows in Standard/Explore modes) */}
        {showSummary && (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: "stretch",
              borderRadius: 3,
              overflow: "hidden",
              mb: 2,
              height: { xs: "auto", md: 150 },
            }}
          >
            {/* Left — cost content */}
            <Box
              sx={{
                flex: 1,
                px: { xs: 2, md: 2.5 },
                py: { xs: 1.5, md: 0 },
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                textAlign: "left",
                overflow: "hidden",
                bgcolor: "transparent",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 0 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.1, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {submittedDestination?.city || "Your destination"}
                </Typography>
                {(onShareSearch || onSaveSearch) && (
                  <Box sx={{ display: "flex", gap: 0.25, flexShrink: 0 }}>
                    {onShareSearch && (
                      <Tooltip title="Copy a link to this search">
                        <IconButton
                          size="small"
                          onClick={onShareSearch}
                          aria-label="share search"
                          sx={{
                            color: "text.secondary",
                            "&:hover": { color: theme.palette.primary.main, bgcolor: "action.hover" },
                          }}
                        >
                          <IosShareIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {onSaveSearch && (
                      <Tooltip
                        title={
                          !isPro
                            ? "Save searches (Pro)"
                            : isSearchSaved
                            ? "Remove from saved"
                            : "Save this search"
                        }
                      >
                        <IconButton
                          size="small"
                          onClick={onSaveSearch}
                          aria-label={isSearchSaved ? "remove from saved" : "save search"}
                          sx={{
                            color: isSearchSaved ? theme.palette.primary.main : "text.secondary",
                            "&:hover": {
                              color: theme.palette.primary.main,
                              bgcolor: "action.hover",
                            },
                          }}
                        >
                          {isSearchSaved ? (
                            <BookmarkRoundedIcon fontSize="small" />
                          ) : (
                            <BookmarkBorderIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                )}
              </Box>

              {tripDays > 0 && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                    {tripDays} days
                    {effectiveDep && effectiveRet
                      ? ` · ${new Date(effectiveDep).toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${new Date(effectiveRet).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
                      : ""}
                  </Typography>
                  {submittedDestination?.city && (
                    <Button
                      variant="text"
                      startIcon={<AutoAwesomeIcon sx={{ fontSize: "0.8rem !important" }} />}
                      onClick={() => setPlannerOpen(true)}
                      size="small"
                      sx={{
                        borderRadius: "20px",
                        textTransform: "none",
                        fontSize: "0.72rem",
                        color: theme.palette.secondary.main,
                        px: 1,
                        py: 0,
                        minHeight: "auto",
                        "&:hover": { bgcolor: "rgba(126, 120, 210, 0.08)" },
                      }}
                    >
                      Plan Trip
                    </Button>
                  )}
                </Box>
              )}

              {isCostReady ? (
                <Typography variant="h4" sx={{ fontWeight: 900, color: theme.palette.mode === "dark" ? "white" : "primary.main", letterSpacing: "-0.5px", lineHeight: 1, mb: 0.75 }}>
                  {formatPrice(totalTripCost, currency)}
                </Typography>
              ) : (
                <Box sx={{ mb: 0.75 }}>
                  <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.25 }}>
                    {loadingPhrases[loadingPhraseIdx]}
                  </Typography>
                  <Skeleton variant="text" width={160} height={40} animation="wave" />
                </Box>
              )}

              {!isMobile && (
              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "nowrap", alignItems: "center" }}>
                {isCostReady ? (
                  <>
                    <Chip
                      icon={<FlightTakeoffIcon sx={{ fontSize: "0.85rem !important" }} />}
                      label={`${sortBy.charAt(0).toUpperCase() + sortBy.slice(1)} flight · ${formatPrice(Math.round(selectedFlightPrice), currency)}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.72rem" }}
                    />
                    <Typography variant="caption" sx={{ color: "text.disabled" }}>+</Typography>
                    <Chip
                      icon={<HomeWorkOutlinedIcon sx={{ fontSize: "0.85rem !important" }} />}
                      label={`${selectedStayCategory.charAt(0).toUpperCase() + selectedStayCategory.slice(1)} stay · ${formatPrice(Math.round(selectedStayPrice), currency)}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.72rem" }}
                    />
                  </>
                ) : (
                  <>
                    <Skeleton variant="rounded" width={130} height={24} animation="wave" />
                    <Skeleton variant="rounded" width={130} height={24} animation="wave" />
                  </>
                )}
              </Box>
              )}
            </Box>

            {/* Right — destination image, wider on desktop */}
            <Box
              sx={{
                width: { xs: "100%", md: "55%" },
                minHeight: { xs: 120, md: "auto" },
                flexShrink: 0,
                overflow: "hidden",
                bgcolor: "action.hover",
              }}
            >
              {destinationImage ? (
                <Box
                  component="img"
                  src={destinationImage}
                  alt={submittedDestination?.city}
                  sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
              ) : (
                <Skeleton variant="rectangular" width="100%" height="100%" animation="wave" />
              )}
            </Box>
          </Box>
        )}

        {/* AI Planner Modal (always rendered when destination known, button moved into card above) */}
        {submittedDestination?.city && (
          <TripPlannerModal
            open={plannerOpen}
            onClose={() => setPlannerOpen(false)}
            destination={submittedDestination.city || submittedDestination}
            startDate={departureDate}
            endDate={returnDate}
            isPro={isPro}
          />
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "column", md: "row" }, // Responsive layout
          justifyContent: "center", // Center on small, space out on larger
          alignItems: { xs: "center", md: "flex-start" }, // Center items horizontally on small screens
          gap: 3,
          mt: 5,
          maxWidth: "1200px", // Limit the total container width
          width: "100%", // Ensure full width on smaller screens
          mx: "auto", // Center the container horizontally
          pb: isMobile ? 15 : 4,
        }}
      >
        {/* FLIGHTS CARD SECTION - This is where the new display logic goes */}
        {searchType !== "staysOnly" &&
          !(searchType === "explore" && !tofly) &&
          (!isMobile || mobilePane === "flights") && (
            <Card
              sx={{
                flex: "none",
                width: {
                  xs: "100%",
                  sm: isCustomizeExpanded === "flights" ? "95vw" : (isCustomizeExpanded === "stays" ? "180px" : "550px"),
                  md: isCustomizeExpanded === "flights" ? "1000px" : (isCustomizeExpanded === "stays" ? "180px" : "550px"),
                },
                maxWidth: isCustomizeExpanded === "flights" ? "1100px" : (isCustomizeExpanded === "stays" ? "180px" : "550px"),
                transition: "all 0.35s ease",
                minHeight: isCustomizeExpanded === "stays" ? "auto" : "875px",
                p: isCustomizeExpanded === "stays" ? 1.5 : { xs: 1, sm: 1.5, md: 2 },
                borderRadius: "12px",
                boxShadow:
                  theme.palette.mode === "dark"
                    ? "0 0 12px rgba(0,0,0,0.5)"
                    : "0 4px 16px rgba(0,0,0,0.1)",
                display: "flex",
                flexDirection: "row",
                alignItems: "stretch",
                position: "relative",
                overflow: "hidden",
                backgroundColor:
                  isCustomizeExpanded === "stays"
                    ? (theme.palette.mode === "dark" ? "#252525" : "#f8f9fa")
                    : (theme.palette.mode === "dark" ? "#1e1e1e" : "#ffffff"),
                border:
                  isCustomizeExpanded === "stays"
                    ? (theme.palette.mode === "dark" ? "1px solid #444" : "1px solid #e0e0e0")
                    : (theme.palette.mode === "dark" ? "none" : `1px solid ${theme.palette.divider}`),
              }}
            >
              <>
                <CardContent
                sx={{
                  flexShrink: 0,
                  width: isCustomizeExpanded === "flights" ? "480px" : "100%",
                  mx: "auto", 
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  p: { xs: 1.5, sm: 2, md: 3 },
                  transition: "width 0.35s ease",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: isCustomizeExpanded === "stays" ? "center" : "center",
                    justifyContent: "center",
                    gap: isCustomizeExpanded === "stays" ? 0.5 : 1.5,
                    flexDirection: isCustomizeExpanded === "stays" ? "column" : "row",
                    mb: 0,
                    width: "100%",
                  }}
                >
                  {isCustomizeExpanded === "stays" ? (
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, mt: 1 }}>
                      <img
                        src="flight-icon.svg"
                        alt="Flight Icon"
                        style={{
                          width: "50px",
                          height: "50px",
                          objectFit: "contain",
                          opacity: 0.9,
                        }}
                      />
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 800,
                          textTransform: "uppercase",
                          letterSpacing: 1,
                          fontSize: "0.75rem",
                          color: "text.secondary",
                        }}
                      >
                        Flights
                      </Typography>
                    </Box>
                  ) : (
                    <img
                      src="flight-icon.svg"
                      alt="Flight Logo"
                      style={{
                        width: "90px",
                        height: "60px",
                        objectFit: "contain",
                      }}
                    />
                  )}
                  {isCustomizeExpanded !== "stays" && (
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 800,
                        textAlign: "center",
                      }}
                    >
                      Flights
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: isCustomizeExpanded === "stays" ? 2 : 2, flexDirection: isCustomizeExpanded === "stays" ? "column" : "row", width: "100%", justifyContent: "center" }}>
                {isCustomizeExpanded !== "stays" && (
                <ToggleButtonGroup
                  value={sortBy}
                  exclusive
                  onChange={(e, newValue) => {
                    if (newValue !== null) {
                      handleSortChange(newValue);
                    }
                  }}
                  sx={{
                    mb: 2,
                    borderRadius: "8px",
                    backgroundColor: "#00000",
                    padding: "1px",
                  }}
                >
                  {["best", "cheapest", "fastest"].map((option) => {
                    const labelMap = {
                      best: "Best",
                      cheapest: "Cheapest",
                      fastest: "Fastest",
                    };

                    const priceMap = {
                      best: bestItinerary?.price,
                      cheapest: cheapestItinerary?.price,
                      fastest: fastestItinerary?.price,
                    };

                    const isDisabled = false;

                    return (
                      <Box
                        key={option}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          borderRadius: "8px",
                        }}
                      >
                        <ToggleButton
                          value={option}
                          disabled={isDisabled}
                          sx={{
                            fontSize: "0.875rem",
                            padding: "6px 16px",
                            textAlign: "center",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            "&.Mui-selected": {
                              backgroundColor: "#1976d2",
                              color: "white",
                              "&:hover": {
                                backgroundColor: "#1565c0",
                              },
                            },
                            "&:focus": {
                              backgroundColor: "#1976d2",
                            },
                            "&:active": {
                              backgroundColor: "#1976d2",
                            },
                          }}
                        >
                          <Typography
                            variant="button"
                            sx={{ fontWeight: "bold", mb: 1 }}
                          >
                            {labelMap[option]}
                          </Typography>
                          <Box
                            sx={{
                              height: "20px",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            {priceMap[option] != null && !isNaN(priceMap[option]) && !flightLoading ? (
                              <Typography
                                variant="caption"
                                sx={{
                                  color:
                                    theme.palette.mode === "dark"
                                      ? "grey.300"
                                      : "inherit",
                                }}
                              >
                                {formatPrice(
                                  Math.round(priceMap[option]),
                                  currency,
                                  false
                                )}
                              </Typography>
                            ) : (
                              <Typography
                                variant="caption"
                                sx={{ visibility: "hidden" }}
                              >
                                $0
                              </Typography>
                            )}
                          </Box>
                        </ToggleButton>
                      </Box>
                    );
                  })}
                </ToggleButtonGroup>
                )}

                {/* Expand icon — inline with toggles */}
                {!isMobile && flightResults.length > 1 && !flightLoading && (
                  <Box
                    onClick={() => setIsCustomizeExpanded(isCustomizeExpanded === "flights" ? false : "flights")}
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      border: "2px solid #1976d2",
                      color: isCustomizeExpanded === "flights" ? "white" : "#1976d2",
                      backgroundColor: isCustomizeExpanded === "flights" ? "#1976d2" : "transparent",
                      "&:hover": {
                        backgroundColor: "#1976d2",
                        color: "white",
                        transform: "scale(1.1)",
                      },
                      transition: "all 0.2s ease",
                    }}
                    title={isCustomizeExpanded === "flights" ? "Collapse" : "Compare more flights"}
                  >
                    {isCustomizeExpanded === "flights" ? <CloseFullscreenIcon sx={{ fontSize: 18 }} /> : <OpenInFullIcon sx={{ fontSize: 18 }} />}
                  </Box>
                )}
                </Box>

                {/* No flights match stop filter */}
                {isCustomizeExpanded !== "stays" && flightStopsFiltered && (
                  <Box sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: "action.hover", textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">
                      No {advancedFilters.flightStops.map(s => s === "nonstop" ? "non-stop" : s === "1stop" ? "1-stop" : "2+ stop").join(" or ")} flights found for this route.
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Try adjusting your stop filter to see more options.
                    </Typography>
                  </Box>
                )}

                {isCustomizeExpanded !== "stays" && <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "300px",
                    textAlign: "center",
                    width: "80%", // full width of parent
                  }}
                >
                  {flightLoading ? (
                    <Box sx={{ p: 2, width: "100%" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                        <Skeleton variant="circular" width={40} height={40} />
                        <Skeleton variant="text" width={130} height={28} />
                      </Box>
                      <Skeleton variant="rectangular" height={56} sx={{ mb: 2, borderRadius: 1 }} animation="wave" />
                      <Skeleton variant="text" width="35%" height={40} sx={{ mb: 2 }} />
                      <Skeleton variant="rectangular" height={48} sx={{ mb: 1, borderRadius: 1 }} animation="wave" />
                      <Skeleton variant="rectangular" height={48} sx={{ mb: 1, borderRadius: 1 }} animation="wave" />
                      <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 1 }} animation="wave" />
                    </Box>
                  ) : flightResults.length > 0 && selectedItinerary?.legs ? (
                    <>
                      <Divider
                        sx={{
                          my: 3,
                          borderColor:
                            theme.palette.mode === "dark" ? "#555" : "#ccc",
                          opacity: 0.6,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            textTransform: "uppercase",
                            fontWeight: 500,
                            letterSpacing: 1,
                            color: theme.palette.text.secondary,
                          }}
                        >
                          Outbound Flight
                        </Typography>
                      </Divider>
                      {/* Route Title */}
                      <Box sx={{ width: "100%", textAlign: "left", mb: 2 }}>
                        <Typography
                          variant="h5"
                          fontWeight="bold"
                          sx={{ fontSize: "1.75rem", mb: 2 }}
                        >
                          {flightResults[0].fromCity} to{" "}
                          {flightResults[0].toCity}
                        </Typography>

                        {/* Airline Logo + Name (First Leg) */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 2,
                          }}
                        >
                          {selectedItinerary.airlineLogo && (
                            <img
                              src={selectedItinerary.airlineLogo}
                              alt="Airline Logo"
                              style={{ height: "40px", objectFit: "contain" }}
                            />
                          )}
                          <Typography
                            variant="body1"
                            fontWeight="bold"
                            sx={{ fontSize: "1.2rem", ml: 1 }}
                          >
                            {selectedItinerary.legs?.[0]?.airline}
                          </Typography>
                        </Box>

                        {/* Date and Time Line */}
                        <Typography
                          variant="body2"
                          sx={{ mb: 0.5, fontSize: "1rem" }}
                        >
                          {new Date(departureDate).toLocaleDateString("en-US", {
                            weekday: "short",
                            month: "short",
                            day: "numeric",
                          })}{" "}
                          – {formatTime(selectedItinerary.departureTime)} →{" "}
                          {formatTime(selectedItinerary.arrivalTime)}
                          {isNextDay(
                            selectedItinerary.departureTime,
                            selectedItinerary.arrivalTime
                          )
                            ? " (+1)"
                            : ""}
                        </Typography>

                        {/* Total Duration */}
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          sx={{ mb: 1, fontSize: "1rem" }}
                        >
                          Total Duration: {selectedItinerary.durationText}
                        </Typography>

                        {/* Stops */}
                        {!isDirect && (
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            Stops: {selectedItinerary.legs.length - 1}
                          </Typography>
                        )}

                        {/* Legs / Segments */}
                        {isDirect ? (
                          <Box sx={{ ml: 0, mb: 2 }}>
                            <Typography variant="body2">
                              {selectedItinerary.fromCity} (
                              {selectedItinerary.legs[0].departure.code}) →{" "}
                              {selectedItinerary.toCity} (
                              {selectedItinerary.legs[0].arrival.code})
                            </Typography>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              borderLeft: "1.5px solid",
                              borderColor:
                                theme.palette.mode === "dark" ? "#aaa" : "#333",
                              pl: 2,
                              ml: 1,
                              mb: 2,
                            }}
                          >
                            {selectedItinerary.legs.map((leg, idx) => {
                              const layover = selectedItinerary.layovers?.[idx];
                              const isFirstLeg = idx === 0;
                              const isLastLeg =
                                idx === selectedItinerary.legs.length - 1;
                              const departureCity = isFirstLeg
                                ? selectedItinerary.fromCity
                                : getCityByIATA(leg.departure.code) || leg.departure.city;
                              const arrivalCity = isLastLeg
                                ? selectedItinerary.toCity
                                : getCityByIATA(leg.arrival.code) || leg.arrival.city;
                              const departureCode = leg.departure.code;
                              const arrivalCode = leg.arrival.code;

                              return (
                                <Box key={idx} sx={{ mb: 1 }}>
                                   <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "baseline", gap: 2 }}>
                                    <Typography variant="body2">
                                      {departureCity} ({departureCode}) → {arrivalCity} ({arrivalCode}) – {leg.durationText}
                                    </Typography>
                                    {leg.flightNumber && (
                                      <Typography variant="caption" sx={{ color: "text.secondary", whiteSpace: "nowrap", flexShrink: 0 }}>
                                        {leg.flightNumber}
                                      </Typography>
                                    )}
                                  </Box>

                                  {layover && (
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.3, ml: 1, color: "text.secondary" }}>
                                      <AccessTimeIcon sx={{ fontSize: "0.85rem" }} />
                                      <Typography variant="caption" sx={{ fontStyle: "italic" }}>
                                        {layover.durationLabel} layover · {getCityByIATA(layover.code) || layover.city} ({layover.code})
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              );
                            })}
                          </Box>
                        )}
                      </Box>

                      {(selectedItinerary.returnLegs?.length ?? 0) > 0 && (
                        <>
                          <Divider
                            sx={{
                              my: 3,
                              borderColor:
                                theme.palette.mode === "dark" ? "#555" : "#ccc",
                              opacity: 0.6,
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{
                                textTransform: "uppercase",
                                fontWeight: 500,
                                letterSpacing: 1,
                                color: theme.palette.text.secondary,
                              }}
                            >
                              Return Flight
                            </Typography>
                          </Divider>
                          {/* Route Title */}
                          <Box sx={{ width: "100%", textAlign: "left", mb: 2 }}>
                            <Typography
                              variant="h5"
                              fontWeight="bold"
                              sx={{ fontSize: "1.75rem", mb: 2 }}
                            >
                              {flightResults[0].toCity} to{" "}
                              {flightResults[0].fromCity}
                            </Typography>

                            {/* Airline Logo + Name (First Return Leg) */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 2,
                              }}
                            >
                              {selectedItinerary.returnLegs?.[0]
                                ?.airlineLogo && (
                                <img
                                  src={
                                    selectedItinerary.returnLegs[0].airlineLogo
                                  }
                                  alt="Airline Logo"
                                  style={{
                                    height: "40px",
                                    objectFit: "contain",
                                  }}
                                />
                              )}
                              <Typography
                                variant="body1"
                                fontWeight="bold"
                                sx={{ fontSize: "1.2rem", ml: 1 }}
                              >
                                {selectedItinerary.returnLegs?.[0]?.airline}
                              </Typography>
                            </Box>

                            {/* Date and Time Line */}
                            <Typography
                              variant="body2"
                              sx={{ mb: 0.5, fontSize: "1rem" }}
                            >
                              {new Date(returnDate).toLocaleDateString(
                                "en-US",
                                {
                                  weekday: "short",
                                  month: "short",
                                  day: "numeric",
                                }
                              )}{" "}
                              –{" "}
                              {formatTime(
                                selectedItinerary.returnLegs?.[0]
                                  ?.departure?.time
                              )}{" "}
                              →{" "}
                              {formatTime(
                                selectedItinerary.returnLegs?.[
                                  selectedItinerary.returnLegs.length - 1
                                ]?.arrival?.time
                              )}
                              {isNextDay(
                                selectedItinerary.returnLegs?.[0]
                                  ?.departure?.time,
                                selectedItinerary.returnLegs?.[
                                  selectedItinerary.returnLegs.length - 1
                                ]?.arrival?.time
                              )
                                ? " (+1)"
                                : ""}
                            </Typography>

                            {/* Total Duration */}
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              sx={{ mb: 1, fontSize: "1rem" }}
                            >
                              Total Duration:{" "}
                              {selectedItinerary.returnOverallDurationText
                                ? selectedItinerary.returnOverallDurationText
                                : `${(selectedItinerary.returnLegs ?? []).reduce(
                                    (total, leg) =>
                                      total + (leg.durationRaw || 0),
                                    0
                                  )} min`}
                            </Typography>

                            {/* Stops */}
                            {(selectedItinerary.returnLegs?.length ?? 0) > 1 && (
                              <Typography variant="body2" sx={{ mb: 1 }}>
                                Stops: {selectedItinerary.returnLegs.length - 1}
                              </Typography>
                            )}

                            {/* Legs / Segments */}
                            <Box
                              sx={
                                isReturnDirect
                                  ? { ml: 0, mb: 2, textAlign: "left" } // Styles for DIRECT flight (no line, minimal margin)
                                  : {
                                      // Styles for MULTI-LEG flight (WITH line and its padding/margin)
                                      borderLeft: "1.5px solid",
                                      borderColor:
                                        theme.palette.mode === "dark"
                                          ? "#aaa"
                                          : "#333",
                                      pl: 2, // Padding for content next to the line
                                      ml: 1, // Margin to position the line
                                      mb: 2,
                                      textAlign: "left",
                                    }
                              }
                            >
                              {selectedItinerary.returnLegs.map((leg, idx) => {
                                const nextLeg =
                                  selectedItinerary.returnLegs[idx + 1];

                                let layoverText = null;
                                if (nextLeg) {
                                  const arrivalTime = new Date(
                                    leg.arrival?.time
                                  );
                                  const nextDepartureTime = new Date(
                                    nextLeg.departure?.time
                                  );
                                  const diffMs =
                                    nextDepartureTime - arrivalTime;

                                  if (!isNaN(diffMs)) {
                                    const diffMins = Math.round(
                                      diffMs / (1000 * 60)
                                    );
                                    const hrs = Math.floor(diffMins / 60);
                                    const mins = diffMins % 60;
                                    const durationLabel = `${hrs > 0 ? `${hrs} hr${hrs > 1 ? "s" : ""} ` : ""}${mins} min`;

                                    const city =
                                      getCityByIATA(
                                        leg.arrival?.code
                                      ) || leg.arrival?.airportName;

                                    layoverText = `${durationLabel} layover – ${city} (${leg.arrival?.code})`;
                                  }
                                }
                                const isFirstLeg = idx === 0;
                                const isLastLeg =
                                  idx ===
                                  selectedItinerary.returnLegs.length - 1;
                                const departureCity = isFirstLeg
                                  ? selectedItinerary.toCity
                                  : getCityByIATA(
                                      leg.departure?.code
                                    ) || leg.departure?.airportName;

                                const arrivalCity = isLastLeg
                                  ? selectedItinerary.fromCity
                                  : getCityByIATA(
                                      leg.arrival?.code
                                    ) || leg.arrival?.airportName;

                                const retDepartureCode = leg.departure?.code;
                                const retArrivalCode = leg.arrival?.code;

                                return (
                                  <Box key={`return-${idx}`} sx={{ mb: 1 }}>
                                    <Typography variant="body2">
                                      {departureCity} ({retDepartureCode}) → {arrivalCity} ({retArrivalCode}){!isReturnDirect && ` – ${leg.durationText}`}
                                    </Typography>

                                    {layoverText && (
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.3, ml: 1, color: "text.secondary" }}>
                                        <AccessTimeIcon sx={{ fontSize: "0.85rem" }} />
                                        <Typography variant="caption" sx={{ fontStyle: "italic" }}>
                                          {layoverText}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                );
                              })}
                            </Box>
                          </Box>
                        </>
                      )}


                      {/* Price at bottom center */}
                      <Typography
                        variant="h4"
                        fontWeight="bold"
                        sx={{
                          mt: 2,
                          color: theme.palette.text.main,
                          textAlign: "center",
                          fontSize: "3rem",
                        }}
                      >
                        {formatPrice(selectedItinerary.price, currency)}
                      </Typography>
                      <Box>
                        <Button
                          // --- LOGIC SECTION ---
                          onClick={async () => {
                            setIsLoadingDirectBooking(true);
                            // Open blank tab immediately on user gesture to avoid popup blockers
                            const bookingWindow = window.open("", "_blank");
                            // Inject branded loading page while API call runs
                            if (bookingWindow) {
                              const airlineName = selectedItinerary.legs[0]?.airline ?? "the airline";
                              const airlineLogoSrc = selectedItinerary.airlineLogo ?? "";
                              const isDark = theme.palette.mode === "dark";
                              const flybnbLogoSrc = `${window.location.origin}/logo/${isDark ? "dark" : "light"}-main-logo.svg`;
                              const bg = isDark ? "#121212" : "#f5f5f8";
                              const textColor = isDark ? "#ffffff" : "#000000";
                              const subColor = isDark ? "#aaaaaa" : "#555555";
                              const accent = "#5F5AA2";
                              bookingWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>Taking you to ${airlineName}...</title>
  <link rel="preconnect" href="https://fonts.googleapis.com"/>
  <link href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;600&display=swap" rel="stylesheet"/>
  <style>
    *{margin:0;padding:0;box-sizing:border-box}
    body{background:${bg};color:${textColor};font-family:'Roboto',sans-serif;display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;gap:24px}
    .flybnb-logo{height:56px;object-fit:contain;opacity:0.85}
    .arrow{font-size:2.5rem;color:${subColor}}
    .airline-logo{height:80px;object-fit:contain}
    .logos{display:flex;align-items:center;gap:28px}
    .spinner{width:52px;height:52px;border:4px solid rgba(95,90,162,0.2);border-top-color:${accent};border-radius:50%;animation:spin 0.8s linear infinite}
    @keyframes spin{to{transform:rotate(360deg)}}
    .msg{font-size:1.3rem;color:${subColor};text-align:center;line-height:1.6}
    .airline{color:${accent};font-weight:600}
    .dots span{display:inline-block;animation:blink 1.2s infinite;opacity:0}
    .dots span:nth-child(2){animation-delay:.2s}
    .dots span:nth-child(3){animation-delay:.4s}
    @keyframes blink{0%,80%,100%{opacity:0}40%{opacity:1}}
  </style>
</head>
<body>
  <div class="logos">
    <img class="flybnb-logo" src="${flybnbLogoSrc}" alt="flybnb"/>
    <span class="arrow">→</span>
    ${airlineLogoSrc ? `<img class="airline-logo" src="${airlineLogoSrc}" alt="${airlineName}"/>` : `<span style="font-weight:600;font-size:1.4rem">${airlineName}</span>`}
  </div>
  <div class="spinner"></div>
  <div class="msg">Taking you to <span class="airline">${airlineName}</span> to complete your booking<span class="dots"><span>.</span><span>.</span><span>.</span></span></div>
</body>
</html>`);
                              bookingWindow.document.close();
                            }
                            // 1️⃣ GOOGLE ANALYTICS
                            if (window.gtag) {
                              window.gtag("event", "click_book_flight", {
                                airline: selectedItinerary.legs[0].airline,
                                destination:
                                  selectedItinerary.legs[
                                    selectedItinerary.legs.length - 1
                                  ].arrival.city,
                                price: selectedItinerary.price,
                              });
                            }

                            // ✅ CHECK ENV VARIABLE (Note: env vars are strings, so we check against 'true')
                            const ENABLE_LOGS =
                              process.env.REACT_APP_ENABLE_LOGS === "true";

                            // Helper for conditional logging
                            const debugLog = (...args) => {
                              if (ENABLE_LOGS) console.log(...args);
                            };

                            try {
                              const bookingToken =
                                selectedItinerary.bookingToken;

                              if (!bookingToken) {
                                // No token — skip straight to Kayak
                                console.warn("⚠️ No booking token — falling back to Kayak");
                                throw Object.assign(new Error("no_token"), { skipToKayak: true });
                              }

                              debugLog("🔵 STEP 1: Starting Booking Process");
                              debugLog("🎫 Full Booking Token Found:", bookingToken);

                              // 2️⃣ API CALL 1: GET BOOKING DETAILS
                              debugLog("📡 Calling API: getBookingDetails...");
                              const bookingDetails = await axios.get(
                                "https://google-flights2.p.rapidapi.com/api/v1/getBookingDetails",
                                {
                                  params: {
                                    booking_token: bookingToken,
                                    currency: currency,
                                    language_code: "en-US",
                                    country_code: "US",
                                  },
                                  headers: {
                                    "x-rapidapi-key":
                                      process.env.REACT_APP_RAPIDAPI_KEY,
                                    "x-rapidapi-host":
                                      "google-flights2.p.rapidapi.com",
                                  },
                                }
                              );

                              const allOptions = bookingDetails.data?.data ?? [];
                              console.log(`📋 getBookingDetails returned ${allOptions.length} total options:`);
                              allOptions.forEach((d, i) => {
                                const name = d.title || d.name || d.provider_name || d.id || "Unknown";
                                const isAirline = d.is_airline === true || d.meta?.is_airline === true;
                                const tokenLen = d.token?.length ?? 0;
                                console.log(`   [${i + 1}] ${name} — is_airline: ${isAirline} — token: ${tokenLen} chars`);
                              });

                              // 3️⃣ FILTER FOR AIRLINE OPTIONS ONLY
                              const allAirlineOptions = allOptions.filter(
                                (d) => d.is_airline === true || d.meta?.is_airline === true
                              );
                              console.log(`✈️ Airline-flagged options: ${allAirlineOptions.length}`);

                              let validToken = null;

                              // 4️⃣ PICK FIRST AIRLINE OPTION WITH A NON-EMPTY TOKEN
                              for (const opt of allAirlineOptions) {
                                const tokenLen = opt.token?.length ?? 0;
                                const providerName = opt.title || opt.name || opt.provider_name || opt.id || "Unknown";
                                console.log(`🔍 ${providerName} — token: ${tokenLen} chars — ${tokenLen > 0 ? "✅ using" : "❌ empty, skipping"}`);
                                if (tokenLen > 0) {
                                  validToken = opt.token;
                                  break;
                                }
                              }

                              if (!validToken) {
                                console.warn("⚠️ No valid airline token found → falling back to Kayak");
                              }

                              // 5️⃣ FINAL DECISION: USE TOKEN TO GET URL (API CALL 2)
                              if (validToken) {
                                debugLog(
                                  "🚀 SUCCESS: Valid Token found. Calling getBookingURL API..."
                                );

                                const options = {
                                  method: "POST",
                                  url: "https://google-flights2.p.rapidapi.com/api/v1/getBookingURL",
                                  headers: {
                                    "x-rapidapi-key":
                                      process.env.REACT_APP_RAPIDAPI_KEY,
                                    "x-rapidapi-host":
                                      "google-flights2.p.rapidapi.com",
                                    "Content-Type": "application/json",
                                  },
                                  data: { token: validToken },
                                };

                                try {
                                  const response = await axios.request(options);
                                  debugLog(
                                    "🔗 Booking URL Response:",
                                    response.data
                                  );

                                  const finalUrl = response.data?.data;

                                  if (finalUrl) {
                                    debugLog(
                                      "✈️ Redirecting to Airline:",
                                      finalUrl
                                    );
                                    bookingWindow.location.href = finalUrl;
                                  } else {
                                    console.error(
                                      "❌ API returned success but 'data' field was empty."
                                    );
                                    bookingWindow.close();
                                    alert(
                                      "Could not retrieve the booking link from the airline."
                                    );
                                  }
                                } catch (apiError) {
                                  console.error(
                                    "❌ Error fetching booking URL:",
                                    apiError
                                  );
                                  bookingWindow.close();
                                  alert(
                                    "Failed to generate the direct booking link."
                                  );
                                }

                                setIsLoadingDirectBooking(false);
                                return; // Stop here, do not run Kayak fallback
                              }

                              // 6️⃣ FALLBACK TO KAYAK
                              if (ENABLE_LOGS) {
                                console.warn(
                                  "⚠️ No airline booking token found. Falling back to Kayak."
                                );
                              }

                              // --- Kayak Date Helper ---
                              const formatForKayak = (dateStr) => {
                                if (!dateStr) return "";
                                const cleanDate = dateStr
                                  .split("T")[0]
                                  .split(" ")[0];
                                const parts = cleanDate.split(/[-/]/);

                                if (parts.length === 3) {
                                  // Case A: YYYY-MM-DD
                                  if (parts[0].length === 4) {
                                    const [y, m, d] = parts;
                                    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
                                  }
                                  // Case B: DD-MM-YYYY -> Flip
                                  if (parts[2].length === 4) {
                                    const [d, m, y] = parts;
                                    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
                                  }
                                }
                                return cleanDate; // Fallback
                              };

                              // --- Build Kayak URL ---
                              const origin = selectedItinerary.legs[0].departure.code;
                              const dest = selectedItinerary.legs[selectedItinerary.legs.length - 1].arrival.code;
                              const activeOption = currencyOptions.find(
                                (c) => c.code === currency
                              );
                              const domain =
                                activeOption?.kayakDomain || "kayak.com";

                              // Handle subdomains
                              const protocol = domain.startsWith("kayak")
                                ? "https://www."
                                : "https://";

                              let kayakUrl = `${protocol}${domain}/flights/${origin}-${dest}/${formatForKayak(
                                selectedItinerary.departureTime
                              )}`;

                              if (selectedItinerary.returnLegs?.length > 0) {
                                const rawRetDate =
                                  selectedItinerary.returnLegs[0]
                                    ?.departure?.time || returnDate;
                                kayakUrl += `/${formatForKayak(rawRetDate)}`;
                              }

                              let sortQuery = "?sort=bestflight_a";
                              if (sortBy === "cheapest")
                                sortQuery = "?sort=price_a";
                              else if (sortBy === "fastest")
                                sortQuery = "?sort=duration_a";
                              kayakUrl += sortQuery;

                              debugLog("✈️ Opening Kayak URL:", kayakUrl);
                              bookingWindow.location.href = kayakUrl;
                            } catch (err) {
                              if (err.skipToKayak) {
                                // No token — build and open Kayak URL directly
                                const formatForKayak = (dateStr) => {
                                  if (!dateStr) return "";
                                  const cleanDate = dateStr.split("T")[0].split(" ")[0];
                                  const parts = cleanDate.split(/[-/]/);
                                  if (parts.length === 3) {
                                    if (parts[0].length === 4) {
                                      const [y, m, d] = parts;
                                      return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
                                    }
                                    if (parts[2].length === 4) {
                                      const [d, m, y] = parts;
                                      return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
                                    }
                                  }
                                  return cleanDate;
                                };
                                const orig = selectedItinerary.legs[0]?.departure?.code ?? "";
                                const dest = selectedItinerary.legs[selectedItinerary.legs.length - 1]?.arrival?.code ?? "";
                                const dep = formatForKayak(selectedItinerary.departureTime);
                                let kayakUrl = `https://www.kayak.com/flights/${orig}-${dest}/${dep}`;
                                if (selectedItinerary.returnLegs?.length > 0) {
                                  const retDate = formatForKayak(selectedItinerary.returnLegs[0]?.departure?.time || returnDate);
                                  kayakUrl += `/${retDate}`;
                                }
                                kayakUrl += `?sort=bestflight_a`;
                                debugLog("✈️ SerpAPI → Kayak fallback URL:", kayakUrl);
                                if (bookingWindow) bookingWindow.location.href = kayakUrl;
                              } else {
                                console.error(
                                  "❌ CRITICAL ERROR in booking flow:",
                                  err
                                );
                                bookingWindow.close();
                                alert(
                                  "An error occurred. Please check console for details."
                                );
                              }
                            } finally {
                              setIsLoadingDirectBooking(false);
                            }
                          }}
                          // --- STYLING SECTION (Restored from your original code) ---
                          disabled={isLoadingDirectBooking}
                          sx={{
                            mt: 2,
                            mb: 2,
                            backgroundColor: "#07a1fc", // Original FlyBnB blue
                            color: "white",
                            fontWeight: "bold",
                            borderRadius: "8px",
                            gap: "6px",
                            padding: "12px 24px", // Original large padding
                            fontSize: "1.2rem", // Original large font
                            transition: "all 0.3s ease-in-out",
                            textTransform: "none",
                            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
                            "&:hover": {
                              backgroundColor: "#0590e3",
                              boxShadow: "0 6px 16px rgba(0, 0, 0, 0.3)",
                              transform: "scale(1.05)", // Restored zoom effect
                            },
                            // Ensure button looks okay when disabled (loading)
                            "&.Mui-disabled": {
                              backgroundColor: "#07a1fc",
                              opacity: 0.7,
                              color: "white",
                            },
                          }}
                        >
                          {/* --- CONTENT SECTION (With Loading Spinner) --- */}
                          {isLoadingDirectBooking ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <CircularProgress
                                size={20}
                                sx={{ color: "white" }}
                              />
                              <Typography
                                variant="button"
                                sx={{ textTransform: "none" }}
                              >
                                Generating link...
                              </Typography>
                            </Box>
                          ) : (
                            <>
                              {"Book with "}
                              {selectedItinerary.legs?.[0]?.airline && (
                                <span
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  {selectedItinerary.legs[0].airline}
                                  {selectedItinerary.airlineLogo && (
                                    <Box
                                      component="img"
                                      src={selectedItinerary.airlineLogo}
                                      alt={selectedItinerary.legs[0].airline}
                                      sx={{
                                        width: 38,
                                        height: 38,
                                        ml: 1,
                                        borderRadius: "4px",
                                        backgroundColor: "white",
                                        padding: "2px",
                                        boxShadow: "0 0 4px rgba(0,0,0,0.2)",
                                      }}
                                    />
                                  )}
                                </span>
                              )}
                            </>
                          )}
                        </Button>

                      </Box>
                    </>
                  ) : noFlightsFound && !loading ? (
                    <Typography
                      variant="h6"
                      sx={{ mt: 2, color: "text.primary" }}
                    >
                      No flights found between <br />
                      {submittedOrigin?.city || "your origin"} and{" "}
                      {submittedDestination?.city || "your destination"} for
                      these dates.
                    </Typography>
                  ) : loading ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: "300px",
                        p: 3,
                      }}
                    >
                      <CircularProgress
                        size={50}
                        thickness={4}
                        sx={{ mb: 2 }}
                      />
                      <Typography
                        variant="h6"
                        sx={{
                          color: theme.palette.text.secondary,
                          textAlign: "center",
                        }}
                      >
                        Loading flight details...
                      </Typography>
                    </Box>
                  ) : null}
                </Box>}
              </CardContent>


              {/* Inline alternatives column */}
              {isCustomizeExpanded === "flights" && (
                <Box
                  sx={{
                    flex: 1,
                    borderLeft: `1px solid ${theme.palette.divider}`,
                    overflowY: "auto",
                    maxHeight: "875px",
                    p: 0,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/* Header — title + stops/duration filters */}
                  <Box
                    sx={{
                      p: 2,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      flexWrap: "wrap",
                      position: "sticky",
                      top: 0,
                      bgcolor: theme.palette.mode === "dark" ? "rgba(30,30,30,0.95)" : "rgba(255,255,255,0.95)",
                      backdropFilter: "blur(8px)",
                      zIndex: 2,
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight="800" sx={{ letterSpacing: "0.02em" }}>
                      More Flights
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                      <FormControl size="small" sx={{ minWidth: 110 }}>
                        <Select
                          value={expStopsFilter}
                          onChange={(e) => setExpStopsFilter(e.target.value)}
                          sx={{ fontSize: "0.75rem", borderRadius: "8px", height: 30 }}
                        >
                          <MenuItem value="any" sx={{ fontSize: "0.8rem" }}>Any stops</MenuItem>
                          <MenuItem value="nonstop" sx={{ fontSize: "0.8rem" }}>Nonstop</MenuItem>
                          <MenuItem value="1" sx={{ fontSize: "0.8rem" }}>1 stop</MenuItem>
                          <MenuItem value="2plus" sx={{ fontSize: "0.8rem" }}>2+ stops</MenuItem>
                        </Select>
                      </FormControl>
                      <Button
                        size="small"
                        variant={expDurationMax != null ? "contained" : "outlined"}
                        onClick={(e) => setDurationFilterAnchor(e.currentTarget)}
                        disabled={durationBounds.max <= durationBounds.min}
                        sx={{
                          fontSize: "0.75rem",
                          textTransform: "none",
                          borderRadius: "8px",
                          height: 30,
                          minWidth: 90,
                          whiteSpace: "nowrap",
                          ...(expDurationMax != null
                            ? {}
                            : { color: "text.primary", borderColor: theme.palette.divider }),
                        }}
                      >
                        {expDurationMax != null ? `≤ ${formatDurationMins(expDurationMax)}` : "Duration"}
                      </Button>
                      <Popover
                        open={Boolean(durationFilterAnchor)}
                        anchorEl={durationFilterAnchor}
                        onClose={() => setDurationFilterAnchor(null)}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                      >
                        <Box sx={{ p: 2, width: 240 }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                            <Typography variant="caption" fontWeight="bold" color="text.secondary">
                              Max outbound duration
                            </Typography>
                            {expDurationMax != null && (
                              <Typography
                                variant="caption"
                                onClick={() => setExpDurationMax(null)}
                                sx={{ color: "primary.main", cursor: "pointer", fontWeight: 600 }}
                              >
                                Reset
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ px: 0.5 }}>
                            <Slider
                              value={expDurationMax ?? durationBounds.max}
                              min={durationBounds.min}
                              max={durationBounds.max}
                              onChange={(e, val) => setExpDurationMax(val)}
                              valueLabelDisplay="auto"
                              valueLabelFormat={(v) => formatDurationMins(v)}
                            />
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {formatDurationMins(durationBounds.min)} – {formatDurationMins(durationBounds.max)}
                          </Typography>
                        </Box>
                      </Popover>
                    </Box>
                  </Box>

                  <Box sx={{ p: 2, flex: 1, overflowY: "auto" }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                      {expandedFlights.length} {expandedFlights.length === 1 ? "option" : "options"}
                    </Typography>
                    {expandedFlights.length === 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mt: 3 }}>
                        No flights match these filters.
                      </Typography>
                    )}
                    {expandedFlights.map((flight) => {
                      const isSelected = selectedItinerary?.id === flight.id;
                      const isExpanded = expandedFlightId === flight.id;
                      const stops = Math.max(0, (flight.legs?.length || 1) - 1);
                      const hasReturn = (flight.returnLegs?.length ?? 0) > 0;
                      const outCode0 = flight.legs?.[0]?.departure?.code;
                      const outCodeN = flight.legs?.[flight.legs.length - 1]?.arrival?.code;

                      return (
                        <Box
                          key={flight.id}
                          onClick={async () => {
                            if (isSelected) {
                              setCustomItinerary(null);
                              setExpandedFlightId(null);
                              return;
                            }
                            setCustomItinerary(flight);
                            setExpandedFlightId(flight.id);
                            // Lazy-load return flights for roundtrip itineraries missing them
                            if (
                              flightType === "roundtrip" &&
                              flight.nextToken &&
                              !(flight.returnLegs?.length > 0) &&
                              enrichReturn &&
                              enrichingFlightId !== flight.id
                            ) {
                              setEnrichingFlightId(flight.id);
                              try {
                                const enriched = await enrichReturn(flight);
                                setCustomItinerary((prev) =>
                                  prev && prev.id === enriched.id ? enriched : prev
                                );
                              } finally {
                                setEnrichingFlightId(null);
                              }
                            }
                          }}
                          sx={{
                            p: 1.5,
                            mb: 1.5,
                            border: `2px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider}`,
                            borderRadius: "12px",
                            cursor: "pointer",
                            backgroundColor: isSelected
                              ? (theme.palette.mode === "dark" ? "rgba(95, 90, 162, 0.15)" : "rgba(95, 90, 162, 0.05)")
                              : "transparent",
                            transition: "all 0.15s ease",
                            "&:hover": {
                              borderColor: theme.palette.primary.main,
                              backgroundColor: isSelected ? undefined : (theme.palette.mode === "dark" ? "#252525" : "#f8f9fa"),
                            },
                          }}
                        >
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                              {flight.airlineLogo && (
                                <img src={flight.airlineLogo} alt="" style={{ height: 20, objectFit: "contain" }} />
                              )}
                              <Typography variant="body2" fontWeight="bold" sx={{ fontSize: "0.82rem" }}>
                                {flight.legs?.[0]?.airline || "Airline"}
                              </Typography>
                            </Box>
                            <Typography variant="body2" fontWeight="bold" color="primary" sx={{ fontSize: "0.9rem" }}>
                              {formatPrice(flight.price, currency, false)}
                            </Typography>
                          </Box>

                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: "0.75rem", mb: 0.25 }}>
                            {outCode0} → {outCodeN}
                            {hasReturn ? "  •  Roundtrip" : ""}
                          </Typography>

                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.72rem" }}>
                            {flight.durationText || ""} • {stops === 0 ? "Nonstop" : `${stops} stop${stops > 1 ? "s" : ""}`}
                          </Typography>

                          {isExpanded && (
                            <Box sx={{ mt: 1, pt: 1, borderTop: `1px dashed ${theme.palette.divider}` }}>
                              <Typography variant="caption" fontWeight={800} sx={{ display: "block", textTransform: "uppercase", fontSize: "0.62rem", color: "text.secondary", letterSpacing: "0.05em" }}>
                                Outbound
                              </Typography>
                              {(flight.legs || []).map((leg, i) => (
                                <Typography key={`o-${i}`} variant="caption" sx={{ display: "block", fontSize: "0.72rem", mt: 0.25 }}>
                                  {leg.departure?.code} → {leg.arrival?.code}
                                  {leg.durationText ? ` • ${leg.durationText}` : ""}
                                  {leg.airline ? ` • ${leg.airline}` : ""}
                                </Typography>
                              ))}
                              {enrichingFlightId === flight.id ? (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                                  <CircularProgress size={14} />
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.72rem" }}>
                                    Loading return flights…
                                  </Typography>
                                </Box>
                              ) : hasReturn ? (
                                <>
                                  <Typography variant="caption" fontWeight={800} sx={{ display: "block", mt: 0.75, textTransform: "uppercase", fontSize: "0.62rem", color: "text.secondary", letterSpacing: "0.05em" }}>
                                    Return
                                  </Typography>
                                  {(flight.returnLegs || []).map((leg, i) => (
                                    <Typography key={`r-${i}`} variant="caption" sx={{ display: "block", fontSize: "0.72rem", mt: 0.25 }}>
                                      {leg.departure?.code} → {leg.arrival?.code}
                                      {leg.durationText ? ` • ${leg.durationText}` : ""}
                                      {leg.airline ? ` • ${leg.airline}` : ""}
                                    </Typography>
                                  ))}
                                </>
                              ) : flightType === "roundtrip" ? (
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75, fontSize: "0.72rem", fontStyle: "italic" }}>
                                  Return flights unavailable for this option.
                                </Typography>
                              ) : null}
                            </Box>
                          )}

                          {isSelected && (
                            <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "primary.main", fontWeight: 800, textTransform: "uppercase", fontSize: "0.65rem" }}>
                              ✓ Selected
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}
              </>
            </Card>
          )}

        {/* Stays Card */}
        {(!isMobile || mobilePane === "stays") && (
          <Card
            sx={{
              flex: searchType === "staysOnly" ? "none" : "none",
              width: {
                xs: "100%",
                sm: isCustomizeExpanded === "stays" ? "95vw" : (isCustomizeExpanded === "flights" ? "200px" : "550px"),
                md: isCustomizeExpanded === "stays" ? "1100px" : (isCustomizeExpanded === "flights" ? "200px" : "550px"),
              },
              maxWidth: isCustomizeExpanded === "stays" ? (isMobile ? "100%" : "1200px") : (isCustomizeExpanded === "flights" ? "200px" : "550px"),
              transition: "all 0.35s ease",
              height: isCustomizeExpanded === "stays" ? (isMobile ? "90vh" : "850px") : "auto",
              minHeight: isCustomizeExpanded === "flights" ? "auto" : "600px",
              overflow: "hidden",
              p: isCustomizeExpanded === "flights" ? 2 : (isMobile && isCustomizeExpanded === "stays" ? 0.5 : { xs: 1, sm: 1.5, md: 2 }),
              borderRadius: "10px",
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0 0 12px rgba(0,0,0,0.5)"
                  : "0 4px 16px rgba(0,0,0,0.1)",
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              position: "relative",
              mx: searchType === "staysOnly" ? "auto" : "unset",
              backgroundColor:
                theme.palette.mode === "dark" ? "#1e1e1e" : "#ffffff",
              border: theme.palette.mode === "dark" ? "none" : "1px solid #ddd",
            }}
          >
            <>
              <CardContent sx={{ p: isCustomizeExpanded ? undefined : { xs: 1.5, sm: 2, md: 3 }, ...(showMapView && { pb: "8px !important" }) }}>
              {/* 1) Header + logo always at the top */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  mb: isCustomizeExpanded === "stays" ? 1 : (isCustomizeExpanded === "flights" ? 1 : 2),
                  px: isCustomizeExpanded === "stays" ? 2 : 0,
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: isCustomizeExpanded === "stays" ? "flex-start" : "center", width: "100%" }}>
                  {isCustomizeExpanded === "stays" ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%", mb: 0.5 }}>
                       <img
                        src="stay-icon.svg"
                        alt="stay Logo"
                        style={{
                          width: "50px",
                          height: "35px",
                          objectFit: "contain",
                        }}
                      />
                      <Typography variant="h6" fontWeight="700" sx={{ mr: 1 }}>
                        Airbnb
                      </Typography>
                      {/* Expand icon next to Airbnb — only on desktop */}
                      {!isMobile && (
                      <Box
                        onClick={() => setIsCustomizeExpanded(isCustomizeExpanded === "stays" ? false : "stays")}
                        sx={{
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          backgroundColor: "#FF5A5F",
                          color: "white",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                          "&:hover": {
                            backgroundColor: (theme) => darken("#FF5A5F", 0.1),
                          },
                          transition: "all 0.2s ease",
                        }}
                      >
                        <CloseFullscreenIcon sx={{ fontSize: 16 }} />
                      </Box>
                      )}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: isCustomizeExpanded === "flights" ? 1.5 : 1.5,
                        flexDirection: isCustomizeExpanded === "flights" ? "column" : "row",
                        width: "100%",
                        mt: isCustomizeExpanded === "flights" ? 2 : 0,
                      }}
                    >
                      <img
                        src="stay-icon.svg"
                        alt="stay Logo"
                        style={{
                          width: isCustomizeExpanded === "flights" ? "50px" : "90px",
                          height: isCustomizeExpanded === "flights" ? "35px" : "60px",
                          objectFit: "contain",
                          transition: "all 0.3s ease",
                        }}
                      />
                      <Typography
                        variant={isCustomizeExpanded === "flights" ? "body1" : "h3"}
                        sx={{
                          fontWeight: 800,
                          textAlign: "center",
                          fontSize: isCustomizeExpanded === "flights" ? "0.9rem" : undefined,
                          textTransform: isCustomizeExpanded === "flights" ? "uppercase" : "none",
                          letterSpacing: isCustomizeExpanded === "flights" ? "0.05em" : "normal",
                        }}
                      >
                        Airbnbs
                      </Typography>
                    </Box>
                  )}

                  {isCustomizeExpanded === "stays" && (
                    <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5 }}>
                      <Typography variant="h5" fontWeight="800">
                        {submittedDestination?.city || "Stay Destination"}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8, fontWeight: 500 }}>
                        {departureDate && returnDate ? `${format(new Date(departureDate), "MMM dd")} - ${format(new Date(returnDate), "MMM dd")}` : "Select dates"}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
              {/* 2) ToggleButtons always visible unless expanded */}
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", mb: 0, gap: 1, flexDirection: isCustomizeExpanded === "flights" ? "column" : "row" }}>
              {isCustomizeExpanded !== "stays" && isCustomizeExpanded !== "flights" && (
              <ToggleButtonGroup
                value={selectedStayCategory}
                exclusive
                onChange={(e, newValue) => {
                  if (newValue !== null) {
                    handleStayCategoryChange(e, newValue);
                  }
                }}
                sx={{
                  mb: 2,
                  borderRadius: "8px",
                  backgroundColor: "#00000",
                  padding: "1px",
                }}
              >
                {/* Economy */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    borderRadius: "8px",
                  }}
                >
                  <ToggleButton
                    value="economy"
                    disabled={economyListings.length === 0}
                    sx={{
                      fontSize: "0.875rem",
                      padding: "6px 16px",
                      textAlign: "center",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      "&.Mui-selected": {
                        backgroundColor: "#FF5A5F",
                        color: "white",
                        "&:hover": {
                          backgroundColor: "#FF5A5F",
                        },
                      },
                      "&:focus": { backgroundColor: "#FF5A5F" },
                      "&:active": { backgroundColor: "#FF5A5F" },
                    }}
                  >
                    <Typography
                      variant="button"
                      sx={{ fontWeight: "bold", mb: 1 }}
                    >
                      Economy
                    </Typography>
                    <Box
                      sx={{
                        height: "20px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {economyListings.length > 0 ? (
                        <Typography
                          variant="caption"
                          sx={{
                            color:
                              theme.palette.mode === "dark"
                                ? "grey.300"
                                : "inherit",
                          }}
                        >
                          {formatPrice(
                            Math.round(quartiles.Q1),
                            currency,
                            false
                          )}
                        </Typography>
                      ) : (
                        <Typography
                          variant="caption"
                          sx={{ visibility: "hidden" }}
                        >
                          $0
                        </Typography>
                      )}
                    </Box>
                  </ToggleButton>
                </Box>

                {/* Standard */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    borderRadius: "8px",
                  }}
                >
                  <ToggleButton
                    value="standard"
                    disabled={standardListings.length === 0}
                    sx={{
                      fontSize: "0.875rem",
                      padding: "6px 16px",
                      textAlign: "center",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      "&.Mui-selected": {
                        backgroundColor: "#FF5A5F",
                        color: "white",
                        "&:hover": {
                          backgroundColor: "#FF5A5F",
                        },
                      },
                      "&:focus": { backgroundColor: "#FF5A5F" },
                      "&:active": { backgroundColor: "#FF5A5F" },
                    }}
                  >
                    <Typography
                      variant="button"
                      sx={{ fontWeight: "bold", mb: 1 }}
                    >
                      Standard
                    </Typography>
                    <Box
                      sx={{
                        height: "20px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {standardListings.length > 0 ? (
                        <Typography
                          variant="caption"
                          sx={{
                            color:
                              theme.palette.mode === "dark"
                                ? "grey.300"
                                : "inherit",
                          }}
                        >
                          {formatPrice(
                            Math.round(quartiles.Q2),
                            currency,
                            false
                          )}
                        </Typography>
                      ) : (
                        <Typography
                          variant="caption"
                          sx={{ visibility: "hidden" }}
                        >
                          $0
                        </Typography>
                      )}
                    </Box>
                  </ToggleButton>
                </Box>

                {/* Premium */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    borderRadius: "8px",
                  }}
                >
                  <ToggleButton
                    value="premium"
                    disabled={premiumListings.length === 0}
                    sx={{
                      fontSize: "0.875rem",
                      padding: "6px 16px",
                      textAlign: "center",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      "&.Mui-selected": {
                        backgroundColor: "#FF5A5F",
                        color: "white",
                        "&:hover": {
                          backgroundColor: "#FF5A5F",
                        },
                      },
                      "&:focus": { backgroundColor: "#FF5A5F" },
                      "&:active": { backgroundColor: "#FF5A5F" },
                    }}
                  >
                    <Typography
                      variant="button"
                      sx={{ fontWeight: "bold", mb: 1 }}
                    >
                      Premium
                    </Typography>
                    <Box
                      sx={{
                        height: "20px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {premiumListings.length > 0 ? (
                        <Typography
                          variant="caption"
                          sx={{
                            color:
                              theme.palette.mode === "dark"
                                ? "grey.300"
                                : "inherit",
                          }}
                        >
                          {formatPrice(
                            Math.round(quartiles.Q3),
                            currency,
                            false
                          )}
                        </Typography>
                      ) : (
                        <Typography
                          variant="caption"
                          sx={{ visibility: "hidden" }}
                        >
                          $0
                        </Typography>
                      )}
                    </Box>
                  </ToggleButton>
                </Box>
                }
              </ToggleButtonGroup>
              )}

              {/* Expand icon — inline with stays toggles — hidden when expanded stays — only on desktop */}
              {!isMobile && isCustomizeExpanded !== "stays" && !isMobile && allMapListings.length > 1 && !stayLoading && !loading && (
                <Box
                  onClick={() => setIsCustomizeExpanded(isCustomizeExpanded === "stays" ? false : "stays")}
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    border: "2px solid #FF5A5F",
                    color: isCustomizeExpanded === "stays" ? "white" : "#FF5A5F",
                    backgroundColor: isCustomizeExpanded === "stays" ? "#FF5A5F" : "transparent",
                    "&:hover": {
                      backgroundColor: "#FF5A5F",
                      color: "white",
                      transform: "scale(1.1)",
                    },
                    transition: "all 0.2s ease",
                  }}
                  title={isCustomizeExpanded === "stays" ? "Collapse" : "Compare more stays"}
                >
                  {isCustomizeExpanded === "stays" ? <CloseFullscreenIcon sx={{ fontSize: 18 }} /> : <OpenInFullIcon sx={{ fontSize: 18 }} />}
                </Box>
              )}
              </Box>
              {isCustomizeExpanded !== "flights" && isCustomizeExpanded !== "stays" && (
                <>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    mt: 1,
                    mb: 1,
                  }}
                >
                {/* Combined Filter Summary */}
                <Box
                  sx={{
                    typography: "body2",
                    textAlign: "center",
                    color: "white",
                    fontWeight: 400,
                    fontSize: "0.95rem",
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    gap: 1,
                  }}
                >
                  {(() => {
                    // collect filter labels
                    const filters = [];

                    if (airbnbType !== "both") {
                      filters.push(
                        airbnbType === "entire_home"
                          ? "Entire Home"
                          : "Private Room"
                      );
                    }
                    if (minReviewCount !== 5) {
                      filters.push(`≥ ${minReviewCount} reviews`);
                    }
                    const reviewScoreNumber = parseFloat(minReviewScore);
                    if (
                      !isNaN(reviewScoreNumber) &&
                      reviewScoreNumber !== 4.0
                    ) {
                      filters.push(`≥ ${reviewScoreNumber.toFixed(1)} stars`);
                    }

                    const advancedKeys = Object.keys(advancedFilters).filter(
                      (key) => {
                        const val = advancedFilters[key];
                        return (
                          (typeof val === "boolean" && val) ||
                          (Array.isArray(val) && val.length > 0) ||
                          (typeof val === "number" && val !== 0)
                        );
                      }
                    );

                    // push advanced filters with friendly names
                    advancedKeys.forEach((key) => {
                      if (key.startsWith("amenity_")) {
                        const id = Number(key.replace("amenity_", ""));
                        filters.push(AMENITIES_MAP[id] || `Amenity ${id}`);
                      } else if (
                        key === "superhostOnly" &&
                        advancedFilters[key]
                      ) {
                        filters.push("Superhost Only");
                      } else if (
                        key === "cancelPolicies" &&
                        advancedFilters[key].length > 0
                      ) {
                        filters.push(
                          `Cancellation: ${advancedFilters[key].join(", ")}`
                        );
                      } else if (
                        (key === "minPrice" || key === "maxPrice") &&
                        advancedFilters[key] !== null &&
                        advancedFilters[key] !== undefined
                      ) {
                        filters.push(
                          key === "minPrice"
                            ? `Min $${advancedFilters[key]}`
                            : `Max $${advancedFilters[key]}`
                        );
                      }
                    });

                    // Show first N filters
                    const visibleCount = 8;
                    const visible = filters.slice(0, visibleCount);
                    const hiddenCount = filters.length - visibleCount;

                    return (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                        }}
                      >
                        {/* Filters applied title only if there are filters */}
                        {filters.length > 0 && (
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 600,
                              color: theme.palette.text.primary,
                              mb: 0.5, // margin below title
                            }}
                          >
                            Filters applied:
                          </Typography>
                        )}

                        {/* chips + +N more */}
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            justifyContent: "center",
                            gap: 0.5,
                          }}
                        >
                          {visible.map((text, idx) => (
                            <Chip
                              key={idx}
                              label={text}
                              size="small"
                              sx={{
                                backgroundColor:
                                  theme.palette.mode === "dark"
                                    ? "rgba(255, 255, 255, 0.1)"
                                    : "rgba(0, 0, 0, 0.08)",
                                color:
                                  theme.palette.mode === "dark"
                                    ? theme.palette.common.white
                                    : theme.palette.text.primary,
                                borderRadius: "16px",
                                fontSize: "0.8rem",
                              }}
                            />
                          ))}

                          {hiddenCount > 0 && (
                            <>
                              <Typography component="span" sx={{ mx: 0.5 }}>
                                &
                              </Typography>
                              <Typography
                                component="span"
                                sx={{
                                  color: "primary.main",
                                  fontWeight: 300,
                                  cursor: "pointer",
                                  textDecoration: "underline",
                                }}
                                onClick={() => setFiltersOpen(true)}
                              >
                                {hiddenCount} more
                              </Typography>
                            </>
                          )}
                        </Box>
                      </Box>
                    );
                  })()}
                </Box>

                {/* Clear Filters Text Link */}
                {(airbnbType !== "both" ||
                  minReviewCount !== 5 ||
                  parseFloat(minReviewScore) !== 4.0 ||
                  Object.keys(advancedFilters).length > 0) && (
                  <Typography
                    variant="caption"
                    onClick={() => {
                      handleClearStayFilter();
                    }}
                    sx={{
                      mt: 0.5,
                      color: "error.main",
                      cursor: "pointer",
                      textDecoration: "underline",
                      "&:hover": { color: "error.dark" },
                    }}
                  >
                    Remove Filters
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.75, mb: 0 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  textTransform: "uppercase",
                  fontWeight: 500,
                  letterSpacing: 1,
                  color: theme.palette.text.primary,
                }}
              >
                {submittedDestination
                  ? `${submittedDestination.city}, ${submittedDestination.country_name}`
                  : "Your City"}
              </Typography>

              <Button
                variant="outlined"
                onClick={() => {
                  if (isMobile) {
                    setShowMapView((v) => !v);
                  } else {
                    setIsCustomizeExpanded("stays");
                  }
                }}
                startIcon={<MapIcon />}
                sx={{
                  ml: 0,
                  borderRadius: "20px",
                  textTransform: "none",
                  fontWeight: 600,
                  borderColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.5)"
                      : "rgba(0,0,0,0.2)",
                  color: theme.palette.text.primary,
                  padding: "6px 16px",
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.05)"
                        : "rgba(0,0,0,0.05)",
                  },
                }}
              >
                {isMobile && showMapView ? "Show List" : "Map View"}
              </Button>
              </Box>
              {/* 3) Below toggles: spinner, carousel, or "no results" (map rendered outside CardContent) */}
              {!showMapView && (loading || stayLoading ? (
                <Box sx={{ p: 2 }}>
                  <Skeleton variant="rectangular" height={300} sx={{ mb: 2, borderRadius: 1 }} animation="wave" />
                  <Skeleton variant="text" width="80%" height={28} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="60%" height={22} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="40%" height={20} />
                </Box>
              ) : economyListings.length > 0 ||
                standardListings.length > 0 ||
                premiumListings.length > 0 ? (
                <Box
                  sx={{
                    textAlign: "center",
                    mt: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  {selectedStayCategory === "economy" && (
                    <ImageCarouselWithDetails
                      listings={economyListings}
                      departureDate={departureDate}
                      returnDate={returnDate}
                      totalStayPrice={Math.round(quartiles.Q1)}
                      currency={currency}
                      isPro={isPro}
                    />
                  )}
                  {selectedStayCategory === "standard" && (
                    <ImageCarouselWithDetails
                      listings={standardListings}
                      departureDate={departureDate}
                      returnDate={returnDate}
                      totalStayPrice={Math.round(quartiles.Q2)}
                      currency={currency}
                      isPro={isPro}
                    />
                  )}
                  {selectedStayCategory === "premium" && (
                    <ImageCarouselWithDetails
                      listings={premiumListings}
                      departureDate={departureDate}
                      returnDate={returnDate}
                      totalStayPrice={Math.round(quartiles.Q3)}
                      currency={currency}
                      isPro={isPro}
                    />
                  )}
                </Box>
              ) : noAirbnbFound ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    mt: 3,
                    mb: 3,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      textAlign: "center",
                      color: "white",
                    }}
                  >
                    No Airbnb stays found at{" "}
                    {submittedDestination?.city || "your destination"} on the
                    selected dates. <br />
                    Try different dates & remove filters.
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={handleBackToSearch}
                    sx={{
                      width: 150,
                      height: 50,
                      mt: 3,
                      backgroundColor: "#dc5253",
                      color: "white",
                      fontSize: "1rem",
                      fontWeight: "bold",
                      borderRadius: "8px",
                      "&:hover": {
                        backgroundColor: (theme) => darken("#dc5253", 0.2),
                      },
                    }}
                  >
                    Edit Search
                  </Button>
                </Box>
              ) : null)}
                </>
              )}
            </CardContent>

            {/* Inline Map View — full card width, outside CardContent */}
            {showMapView && (
              <Box sx={{ width: "calc(100% + 16px)", mx: -1, mb: -1 }}>
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    textAlign: "center",
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#2a2a2a" : "#f4f6f8",
                  }}
                >
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {(() => {
                      const fmt = (d) => new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(new Date(d));
                      if (!departureDate) return "";
                      const nights = returnDate
                        ? Math.round((new Date(returnDate) - new Date(departureDate)) / (1000 * 60 * 60 * 24))
                        : null;
                      const dateStr = returnDate
                        ? `${fmt(departureDate)} - ${fmt(returnDate)}`
                        : fmt(departureDate);
                      return nights ? `${dateStr} (${nights} nights)` : dateStr;
                    })()}
                  </Typography>
                </Box>
                <MapView
                  listings={allMapListings}
                  currency={currency}
                  onMarkerClick={(listing) => {
                    setMapListing(listing);
                    setMapAnalyzeOpen(true);
                  }}
                />
              </Box>
            )}

            {/* ✅ CONNECT NAVIGATION LOGIC */}
            {mapListing && (
              <AnalyzeAirbnbModalDashboard
                open={mapAnalyzeOpen}
                onClose={() => setMapAnalyzeOpen(false)}
                listingId={mapListing.id}
                price={mapListing.price}
                departureDate={departureDate}
                returnDate={returnDate}
                isPro={isPro}
                amenityIds={mapListing.amenityIds}
                currency={currency}
                onNext={() => handleMapNavigate("next")}
                onPrev={() => handleMapNavigate("prev")}
                hasNext={
                  allMapListings.findIndex((s) => s.id === mapListing.id) <
                  allMapListings.length - 1
                }
                hasPrev={
                  allMapListings.findIndex((s) => s.id === mapListing.id) > 0
                }
              />
            )}




            {/* Inline stay alternatives — full width below header */}
            {isCustomizeExpanded === "stays" && (() => {
              // Build filtered + sorted list
              const tierMap = {
                economy: economyListings || [],
                standard: standardListings || [],
                premium: premiumListings || [],
                all: allMapListings || [],
              };
              const tierListings = tierMap[staysTier] || allMapListings || [];
              const getStayPrice = (s) => s?.price?.rate || s?.price?.total || s?.price || 0;

              // Price bounds for the current tier
              const tierPrices = tierListings.map(getStayPrice).filter((p) => p > 0);
              const priceBounds = tierPrices.length
                ? { min: Math.floor(Math.min(...tierPrices)), max: Math.ceil(Math.max(...tierPrices)) }
                : { min: 0, max: 0 };
              const effectiveRange = staysPriceRange || [priceBounds.min, priceBounds.max];

              const sorted = [...tierListings]
                .filter((s) => {
                  const p = getStayPrice(s);
                  return p >= effectiveRange[0] && p <= effectiveRange[1];
                })
                .sort((a, b) => {
                  if (staysSort === "rating") return (b.rating || 0) - (a.rating || 0);
                  if (staysSort === "reviews") return (b.reviewCount || b.reviews || 0) - (a.reviewCount || a.reviews || 0);
                  if (staysSort === "price_low") return getStayPrice(a) - getStayPrice(b);
                  if (staysSort === "price_high") return getStayPrice(b) - getStayPrice(a);
                  return 0;
                });

              return (
                <Box sx={{ width: "100%", display: "flex", flexDirection: isMobile ? "column" : "row", flex: 1, overflow: isMobile ? "auto" : "hidden", height: isMobile ? "auto" : "calc(100% - 100px)" }}>
                  {/* Left column — list with controls (Narrower) */}
                  <Box
                    sx={{
                      width: isMobile ? "100%" : "40%",
                      display: "flex",
                      flexDirection: "column",
                      overflowY: isMobile ? "visible" : "auto",
                      height: isMobile ? "auto" : "100%",
                      borderRight: isMobile ? "none" : `1px solid ${theme.palette.divider}`,
                      borderBottom: isMobile ? `1px solid ${theme.palette.divider}` : "none",
                      "&::-webkit-scrollbar": { width: "6px" },
                      "&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(0,0,0,0.1)", borderRadius: "3px" },
                    }}
                  >
                    {/* Sort & Filter bar (Persistent) */}
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        p: 1.5,
                        pb: 1,
                        alignItems: "center",
                        flexWrap: "wrap",
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        position: "sticky",
                        top: 0,
                        backgroundColor: theme.palette.mode === "dark" ? "#1e1e1e" : "#ffffff",
                        zIndex: 10,
                      }}
                    >
                      <FormControl size="small" sx={{ minWidth: 110 }}>
                        <Select
                          value={staysSort}
                          onChange={(e) => setStaysSort(e.target.value)}
                          sx={{ fontSize: "0.75rem", borderRadius: "8px", height: 30 }}
                        >
                          <MenuItem value="rating" sx={{ fontSize: "0.8rem" }}>★ Rating</MenuItem>
                          <MenuItem value="reviews" sx={{ fontSize: "0.8rem" }}># Reviews</MenuItem>
                          <MenuItem value="price_low" sx={{ fontSize: "0.8rem" }}>$ Low → High</MenuItem>
                          <MenuItem value="price_high" sx={{ fontSize: "0.8rem" }}>$ High → Low</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 100 }}>
                        <Select
                          value={staysTier}
                          onChange={(e) => { setStaysTier(e.target.value); setStaysPriceRange(null); }}
                          sx={{ fontSize: "0.75rem", borderRadius: "8px", height: 30 }}
                        >
                          <MenuItem value="all" sx={{ fontSize: "0.8rem" }}>All Tiers</MenuItem>
                          <MenuItem value="economy" sx={{ fontSize: "0.8rem" }}>Economy</MenuItem>
                          <MenuItem value="standard" sx={{ fontSize: "0.8rem" }}>Standard</MenuItem>
                          <MenuItem value="premium" sx={{ fontSize: "0.8rem" }}>Premium</MenuItem>
                        </Select>
                      </FormControl>
                      <Button
                        size="small"
                        variant={staysPriceRange ? "contained" : "outlined"}
                        onClick={(e) => setPriceFilterAnchor(e.currentTarget)}
                        disabled={priceBounds.max <= priceBounds.min}
                        sx={{
                          fontSize: "0.75rem",
                          textTransform: "none",
                          borderRadius: "8px",
                          height: 30,
                          minWidth: 90,
                          whiteSpace: "nowrap",
                          ...(staysPriceRange
                            ? { backgroundColor: "#FF5A5F", "&:hover": { backgroundColor: darken("#FF5A5F", 0.1) } }
                            : { color: "text.primary", borderColor: theme.palette.divider }),
                        }}
                      >
                        {staysPriceRange
                          ? `${formatPrice(effectiveRange[0], currency, false)} – ${formatPrice(effectiveRange[1], currency, false)}`
                          : "$ Price"}
                      </Button>
                      <Popover
                        open={Boolean(priceFilterAnchor)}
                        anchorEl={priceFilterAnchor}
                        onClose={() => setPriceFilterAnchor(null)}
                        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                      >
                        <Box sx={{ p: 2, width: 250 }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                            <Typography variant="caption" fontWeight="bold" color="text.secondary">
                              Price per night
                            </Typography>
                            {staysPriceRange && (
                              <Typography
                                variant="caption"
                                onClick={() => setStaysPriceRange(null)}
                                sx={{ color: "#FF5A5F", cursor: "pointer", fontWeight: 600 }}
                              >
                                Reset
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ px: 0.5 }}>
                            <Slider
                              value={effectiveRange}
                              min={priceBounds.min}
                              max={priceBounds.max}
                              onChange={(e, val) => setStaysPriceRange(val)}
                              valueLabelDisplay="auto"
                              valueLabelFormat={(v) => formatPrice(v, currency, false)}
                              sx={{ color: "#FF5A5F" }}
                            />
                          </Box>
                          <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                            <TextField
                              label="Min"
                              size="small"
                              type="number"
                              value={effectiveRange[0]}
                              onChange={(e) => {
                                const v = Math.max(priceBounds.min, Math.min(Number(e.target.value), effectiveRange[1]));
                                setStaysPriceRange([v, effectiveRange[1]]);
                              }}
                              inputProps={{ style: { fontSize: "0.8rem" } }}
                              sx={{ flex: 1 }}
                            />
                            <TextField
                              label="Max"
                              size="small"
                              type="number"
                              value={effectiveRange[1]}
                              onChange={(e) => {
                                const v = Math.min(priceBounds.max, Math.max(Number(e.target.value), effectiveRange[0]));
                                setStaysPriceRange([effectiveRange[0], v]);
                              }}
                              inputProps={{ style: { fontSize: "0.8rem" } }}
                              sx={{ flex: 1 }}
                            />
                          </Box>
                        </Box>
                      </Popover>
                      <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
                        {sorted.length} stays
                      </Typography>
                    </Box>
                    {/* Listing cards */}
                    <Box sx={{ p: 1, pt: 0.5 }}>
                      {sorted.map((stay, idx) => (
                        <Box
                          key={stay.id || idx}
                          onClick={() => {
                            setMapListing(stay);
                            // We don't open modal immediately; it triggers the MapView popup via prop
                          }}
                          sx={{
                            display: "flex",
                            gap: 1,
                            p: 1,
                            mb: 0.75,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: "8px",
                            cursor: "pointer",
                            transition: "all 0.15s ease",
                            "&:hover": {
                              borderColor: "#FF5A5F",
                              backgroundColor: theme.palette.mode === "dark" ? "rgba(255,90,95,0.06)" : "rgba(255,90,95,0.04)",
                            },
                          }}
                        >
                          <Box
                            component="img"
                            src={stay.images?.[0] || "/placeholder-stay.jpg"}
                            alt={stay.name}
                            sx={{ width: 100, height: 75, borderRadius: "6px", objectFit: "cover", flexShrink: 0 }}
                          />
                          <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                            <Typography variant="body2" fontWeight="bold" noWrap sx={{ fontSize: "0.95rem" }}>
                              {stay.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: "0.85rem" }}>
                              {stay.roomType || "Entire place"} • {stay.rating ? `★ ${stay.rating}` : "New"}
                              {(stay.reviewCount || stay.reviews || stay.reviewsCount || stay.reviews_count || stay.ratingCount) ? (
                                <Typography component="span" sx={{ fontSize: "0.85rem", color: "text.secondary", ml: 0.5 }}>
                                  ({stay.reviewCount || stay.reviews || stay.reviewsCount || stay.reviews_count || stay.ratingCount} reviews)
                                </Typography>
                              ) : ""}
                            </Typography>
                            <Typography variant="caption" fontWeight="bold" sx={{ color: "#FF5A5F", mt: 0.25, fontSize: "0.9rem" }}>
                              {formatPrice(stay.price?.rate || stay.price?.total || stay.price, currency, false)}/night
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  {/* Right column — Map (Wider) */}
                  <Box sx={{ width: isMobile ? "100%" : "60%", height: isMobile ? "400px" : "100%", flexShrink: 0 }}>
                    <MapView
                      listings={sorted.length > 0 ? sorted : allMapListings}
                      currency={currency}
                      selectedListing={mapListing}
                      onMarkerClick={(listing) => {
                        setMapListing(listing);
                        setMapAnalyzeOpen(true);
                      }}
                    />
                  </Box>
                </Box>
              );
            })()}
            </>
          </Card>
        )}
      </Box>
    </>
  );
};

export default Results;
